/**
 * Vista: Detalle/Edición de Check List de Ensamble
 * Formato: AMX-PR-FR-02
 *
 * --- Acordeón de secciones (Ensamble / Funcionamiento y plantilla nueva) ---
 * Regla de producto: solo puede haber UNA sección expandida a la vez en el mismo listado.
 * Al abrir una sección se cierran las demás; al pulsar de nuevo la misma cabecera solo esa se cierra
 * (pueden quedar todas colapsadas).
 *
 * Estado en edición (checklist existente):
 * - `_seccionesAbiertasEx`: mapa categoria -> boolean. Se limpia al cambiar de tab principal (torque, niveles, etc.).
 * - `_isSecBodyVisible(body)`: usa getComputedStyle(display), no solo style.display inline, para no desincronizar el mapa.
 * - `_syncSeccionesAbiertasExDesdeDom(categorias)`: antes de reemplazar innerHTML del mismo tab, copia del DOM qué
 *   sección está visible; normaliza a una sola `true` si hubiera varias (legado).
 * - `_restaurarSeccionesAbiertas()`: tras el re-render, deja abierta como máximo una sección (la primera marcada en el mapa).
 *
 * IDs DOM: cuerpos `secBodyEx-${categoria}` / `secBody-${categoria}` (plantilla); chevrones `chevronEx-*` / `chevron-*`.
 * PNC (`producto_no_conforme`): bloque bajo Observaciones; mismos botones C / I / — que el resto del checklist (pendiente validar con negocio).
 * Ver también docs/CHANGELOG_KAREN.md sección [2026-04-06] Checklist acordeón.
 */

import produccionService from '../services/produccion.service.js';
import tractorService from '../services/tractor.service.js';
import personaService from '../services/persona.service.js';
import recepcionService from '../services/recepcion.service.js';
import calidadService from '../services/calidad.service.js';
import api from '../services/api.js';
import PrintChecklistEnsamble from './PrintChecklistEnsamble.js';
import router from '../router/router.js';
import permisos from '../services/permissions.service.js';

export default {
  /** Categoría de plantilla/ítems para bloque PNC (tab Observaciones y creación) */
  PNC_CATEGORIA: 'producto_no_conforme',

  _pendingSaves: [],

  _showUnsaved() {
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  data: {
    checklist: null,
    tractor: null,
    plantilla: null,
    tecnicos: [],           // Técnicos de producción
    responsablesLinea: [],  // Responsables de línea
    responsablesArea: [],   // Responsables de área
    isNew: false,
    isReadOnly: false
  },

  async render(params) {
    // Es nuevo si no hay id o si el id es 'nuevo'
    this.data.isNew = !params.id || params.id === 'nuevo';

    return `
      <div class="card">
        <div class="card-header" style="display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 1rem;">
          <div>
            <h2 class="card-title">${this.data.isNew ? 'Nuevo Check List de Ensamble' : 'Check List de Ensamble'}</h2>
            <p id="headerSubtitle" style="margin: 0.5rem 0 0 0; color: var(--gray-600);">Formato AMX-PR-FR-02</p>
          </div>
          <div id="headerButtons" style="display: flex; gap: 0.5rem; flex-wrap: wrap;">
            <button class="btn btn-secondary" onclick="router.navigate('/produccion/checklists')">
              ← Volver
            </button>
          </div>
        </div>

        <div id="checklistContent">
          <div style="text-align: center; padding: 2rem;">
            Cargando...
          </div>
        </div>
      </div>
    `;
  },

  _injectStyles() {
    if (document.getElementById('checklistDetalleStyles')) return;
    const style = document.createElement('style');
    style.id = 'checklistDetalleStyles';
    style.textContent = `
      .cl-tabs-bar { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0; padding: 0; border-bottom: 2px solid #e5e7eb; margin-bottom: 1rem; }
      .cl-tab { padding: 0.6rem 0.5rem; border: none; border-bottom: 3px solid transparent; margin-bottom: -2px; background: transparent; color: #6b7280; font-size: 0.8rem; font-weight: 500; cursor: pointer; transition: all 0.2s ease; text-align: center; }
      .cl-tab:hover:not(.active) { color: #374151; border-bottom-color: #d1d5db; }
      .cl-tab.active { color: var(--primary); font-weight: 600; border-bottom-color: var(--primary); }
      @media (max-width: 768px) {
        .ot-grid-firmas { grid-template-columns: 1fr !important; }
      }
    `;
    document.head.appendChild(style);
  },

  async afterRender(params) {
    window.checklistDetalleView = this;
    this._injectStyles();

    // Cargar lista de responsables para los selects de firmas
    await this.cargarResponsables();

    if (this.data.isNew) {
      await this.cargarPlantilla();
      this.renderFormularioNuevo();
    } else {
      await this.cargarChecklist(params.id);
    }
  },

  async cargarResponsables() {
    try {
      // Fuente unificada: usuarios activos con persona asociada.
      const response = await api.get('/usuarios/responsables');
      const todos = Array.isArray(response.data) ? response.data : (response.data?.usuarios || []);
      const responsables = todos
        .filter(u => u.persona_id)
        .map(u => ({
          id: u.persona_id,
          nombre_completo: u.persona_nombre_completo || u.nombre_completo
        }));

      // Los tres selects usan el mismo catálogo
      this.data.tecnicos = responsables;
      this.data.responsablesLinea = responsables;
      this.data.responsablesArea = responsables;
    } catch (error) {
      console.error('Error al cargar responsables:', error);
      this.data.tecnicos = [];
      this.data.responsablesLinea = [];
      this.data.responsablesArea = [];
    }
  },

  async cargarPlantilla() {
    try {
      const response = await produccionService.obtenerPlantillaChecklist();
      this.data.plantilla = response.data || response;
    } catch (error) {
      console.error('Error al cargar plantilla:', error);
      this.data.plantilla = {};
    }
  },

  async cargarChecklist(id) {
    try {
      const response = await produccionService.obtenerChecklist(id);
      this.data.checklist = response.data || response;
      this.data.isReadOnly = this.data.checklist.completado;
      // Acordeon: no reutilizar secciones abiertas de otro checklist en la misma sesion SPA
      this._seccionesAbiertasEx = {};

      // Resetear signaturePads previos: si quedaran vivos en memoria, al volver
      // a guardar se re-subirían las firmas ya persistidas. loadSignaturePad
      // los crea nuevos para los canvases que se rendericen (firmas pendientes).
      this.signaturePadTecnico = null;
      this.signaturePadLinea = null;
      this.signaturePadArea = null;

      // Cargar info de PNC abierto para banner (no bloquea si falla)
      await this.cargarPNCAbierto();

      this.actualizarHeader();
      this.renderChecklistExistente();
    } catch (error) {
      toast.error('Error al cargar check list');
      console.error(error);
      router.navigate('/produccion/checklists');
    }
  },

  async cargarPNCAbierto() {
    this.data.pncAbierto = null;
    try {
      const numSerie = this.data.checklist?.num_serie;
      if (!numSerie) return;
      const resp = await calidadService.obtenerPNCAbiertoPorTractor(numSerie);
      this.data.pncAbierto = resp.data || null;
    } catch (err) {
      console.warn('No se pudo cargar PNC abierto:', err.message);
    }
  },

  _renderBannerPNC() {
    const pnc = this.data.pncAbierto;
    if (!pnc) return '';
    const fp = (pnc.fecha_deteccion || '').substring(0, 10).split('-');
    const fechaStr = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : '-';
    const origenLabel = {
      recepcion: 'Recepción',
      checklist_ensamble: 'Checklist Ensamble',
      manual: 'Registro manual'
    }[pnc.origen_modulo] || pnc.origen_modulo || 'Origen desconocido';
    const totalProblemas = (pnc.problemas || []).length;
    return `
      <div style="border: 2px solid #f59e0b; background: #fef3c7; color: #78350f; border-radius: 8px; padding: 1rem 1.25rem; margin-bottom: 1.5rem; display: flex; align-items: flex-start; gap: 1rem;">
        <div style="font-size: 1.5rem; line-height: 1;">⚠️</div>
        <div style="flex: 1;">
          <div style="font-weight: 700; margin-bottom: 0.35rem;">Este tractor tiene un PNC abierto</div>
          <div style="font-size: 0.85rem; line-height: 1.5;">
            PNC #${pnc.id} — abierto desde <strong>${origenLabel}</strong> el ${fechaStr}.
            ${totalProblemas > 0 ? `Tiene <strong>${totalProblemas}</strong> problema(s) registrado(s).` : ''}
            <br>Los items que marques como <strong>Corregir</strong> se agregarán automáticamente a este PNC al completar el checklist.
          </div>
        </div>
        <button class="btn btn-secondary" style="white-space: nowrap;" onclick="router.navigate('/calidad/pnc/${pnc.id}')">
          Ver PNC
        </button>
      </div>
    `;
  },

  actualizarHeader() {
    const c = this.data.checklist;
    const subtitle = document.getElementById('headerSubtitle');
    const buttons = document.getElementById('headerButtons');

    if (subtitle) {
      const fp = (c.fecha || '').substring(0, 10).split('-');
      const fecha = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : '-';
      subtitle.textContent = `Formato AMX-PR-FR-02 | ${fecha}`;
    }

    if (buttons) {
      buttons.innerHTML = `
        <button class="btn btn-secondary" onclick="router.navigate('/produccion/checklists')">
          ← Volver
        </button>
        ${c.completado && permisos.canPrint('checklists') ? `
          <button class="btn btn-primary" onclick="window.checklistDetalleView.imprimir()">
            🖨️ Imprimir
          </button>
          <button class="btn btn-success" onclick="window.checklistDetalleView.descargarPDF()">
            📄 Descargar PDF
          </button>
        ` : ''}
      `;
    }
  },

  imprimir() {
    router.navigate(`/produccion/checklists/${this.data.checklist.id}/print`);
  },

  async descargarPDF() {
    const c = this.data.checklist;
    if (!c) return;

    toast.info('Generando PDF...');

    try {
      // Cargar html2pdf.js desde CDN si no está cargado
      if (typeof html2pdf === 'undefined') {
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2pdf.js/0.10.2/html2pdf.bundle.min.js';
          script.onload = resolve;
          script.onerror = () => reject(new Error('Error al cargar librería PDF'));
          document.head.appendChild(script);
        });
      }

      // Obtener plantilla para merge de items
      const plantillaResponse = await produccionService.obtenerPlantillaChecklist().catch(() => ({ data: {} }));
      const plantilla = plantillaResponse.data || plantillaResponse || {};

      // Generar HTML usando PrintChecklistEnsamble
      const html = PrintChecklistEnsamble.generateHTML(c, plantilla);

      // Crear contenedor temporal fuera de la vista (html2canvas lo renderiza igual)
      const container = document.createElement('div');
      container.style.cssText = 'position:fixed; top:0; left:-9999px; width:297mm; overflow:hidden;';
      container.innerHTML = html;
      document.body.appendChild(container);

      // Esperar a que las imágenes carguen
      const imgs = container.querySelectorAll('img');
      await Promise.all(Array.from(imgs).map(img =>
        img.complete ? Promise.resolve() : new Promise(r => { img.onload = r; img.onerror = r; })
      ));

      // Quitar margin-bottom de la última página para evitar hoja extra en el PDF
      const lastPage = container.querySelector('.print-page:last-child');
      if (lastPage) lastPage.style.marginBottom = '0';

      // Capturar solo el contenido (.print-cl-container)
      const target = container.querySelector('.print-cl-container') || container;

      // Generar y descargar PDF
      await html2pdf().set({
        margin: 0,
        filename: `CheckList_${c.num_serie}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, scrollY: 0 },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: 'avoid-all', before: '.page-2' }
      }).from(target).save();

      // Limpiar
      document.body.removeChild(container);
      toast.success('PDF descargado');
    } catch (error) {
      toast.error('Error al generar PDF');
      console.error(error);
    }
  },

  /**
   * Limpia el estado de captura del formulario de creación.
   * Necesario porque la vista es un singleton (`export default { ... }`) reutilizado
   * por el router: `_itemsEstados`/`_itemsValores` sobreviven entre navegaciones y, si no
   * se resetean, un checklist nuevo hereda las respuestas del anterior (bug: secciones
   * autollenadas como "Completo"). Ver docs/CHANGELOG_KAREN.md [2026-07-17].
   */
  _resetEstadoCreacion() {
    this._itemsEstados = {};
    this._itemsValores = {};
    this._observacionesCrear = '';
    this._currentTabCrear = null;
  },

  renderFormularioNuevo() {
    const container = document.getElementById('checklistContent');

    // La vista es un singleton reutilizado por el router: si no limpiamos el
    // estado de captura, las marcas C/I/— de una creación anterior se arrastran
    // al siguiente checklist y aparecen todas las secciones como "Completo".
    this._resetEstadoCreacion();

    const hoy = `${new Date().getFullYear()}-${String(new Date().getMonth()+1).padStart(2,'0')}-${String(new Date().getDate()).padStart(2,'0')}`;

    container.innerHTML = `
      <div style="padding: 1rem;">
        <!-- Búsqueda por NS -->
        <div style="border: 1px solid var(--gray-200); padding: 1.25rem; border-radius: 8px; margin-bottom: 1.5rem; background: var(--bg-secondary); box-shadow: 0 1px 3px rgba(0,0,0,0.08);">
          <div style="margin-bottom: 1rem;">
            <h4 style="margin: 0 0 0.25rem; font-size: 0.95rem; font-weight: 600;">Buscar por número de serie</h4>
            <p style="margin: 0; font-size: 0.8rem; color: var(--gray-500);">Ingrese el número de serie del tractor para cargar sus datos automáticamente</p>
          </div>
          <div style="display: flex; gap: 0.75rem; align-items: center;">
            <input type="text" id="numSerie" class="form-input" placeholder="Ej: TE354-2026-0127" style="flex: 1;" />
            <button type="button" class="btn btn-primary" onclick="window.checklistDetalleView.buscarTractorNuevo()">Buscar</button>
          </div>
          <div id="tractorResult" style="margin-top: 1rem;"></div>
        </div>

        <form id="formChecklist" onsubmit="event.preventDefault(); window.checklistDetalleView.guardarNuevo(event);">

        <!-- Formulario principal (oculto hasta encontrar tractor) -->
        <div id="formularioPrincipal" style="display: none;">

          <!-- Datos del Tractor -->
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
                <input type="text" id="marcaInput" class="form-input" readonly style="background: var(--gray-50);" />
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
                <input type="text" id="modeloInput" class="form-input" readonly style="background: var(--gray-50);" />
              </div>
            </div>
            <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha *</label>
                <input type="date" name="fecha" class="form-input" value="${hoy}" required />
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Horas Motor</label>
                <input type="number" name="horas_motor" class="form-input" step="0.01" min="0" value="0" />
              </div>
            </div>
          </div>

          <!-- Responsables -->
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Responsables <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Técnico de Producción *</label>
                <select name="tecnico_ensamble_id" class="form-select" required>
                  <option value="">-- Seleccionar --</option>
                  ${(this.data.tecnicos || []).map(r => `
                    <option value="${r.id}" data-nombre="${r.nombre_completo}">${r.nombre_completo}</option>
                  `).join('')}
                </select>
              </div>
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Responsable de Línea de Producto *</label>
                <select name="responsable_linea_id" class="form-select" required>
                  <option value="">-- Seleccionar --</option>
                  ${(this.data.responsablesLinea || []).map(r => `
                    <option value="${r.id}" data-nombre="${r.nombre_completo}">${r.nombre_completo}</option>
                  `).join('')}
                </select>
              </div>
            </div>
            <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;">
              <div>
                <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Responsable de Área de Producción *</label>
                <select name="responsable_area_id" class="form-select" required>
                  <option value="">-- Seleccionar --</option>
                  ${(this.data.responsablesArea || []).map(r => `
                    <option value="${r.id}" data-nombre="${r.nombre_completo}">${r.nombre_completo}</option>
                  `).join('')}
                </select>
              </div>
            </div>
          </div>

          <!-- Tabs de creación -->
          <div class="cl-tabs-bar" style="display: grid; grid-template-columns: repeat(3, 1fr);">
            <button type="button" class="cl-tab active" onclick="window.checklistDetalleView._showTabCrear('ensamble')">Ensamble</button>
            <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCrear('observaciones')">Observaciones</button>
            <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCrear('funcionamiento')">Funcionamiento</button>
          </div>
          <div id="tabContentCrear" style="margin-bottom: 1.5rem;"></div>

          <!-- Botones -->
          <div style="display: flex; gap: 1rem; justify-content: flex-end;">
            <button type="button" class="btn btn-secondary" onclick="router.navigate('/produccion/checklists')">
              Cancelar
            </button>
            <button type="submit" class="btn btn-primary">
              Crear Check List
            </button>
          </div>
        </div>
      </form>
    `;

    // Enter en input de NS
    document.getElementById('numSerie').addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.buscarTractorNuevo();
      }
    });

    // Ocultar formulario si cambia el número de serie
    document.getElementById('numSerie').addEventListener('input', () => {
      if (this.data.tractor) {
        this.data.tractor = null;
        const fp = document.getElementById('formularioPrincipal');
        if (fp) fp.style.display = 'none';
        const result = document.getElementById('tractorResult');
        if (result) result.innerHTML = '';
      }
    });
  },

  async buscarTractorNuevo() {
    const numSerie = document.getElementById('numSerie').value.trim();
    if (!numSerie) {
      toast.error('Ingrese un número de serie');
      return;
    }

    const resultEl = document.getElementById('tractorResult');
    resultEl.innerHTML = '<p style="color: var(--text-muted);">Buscando tractor...</p>';

    try {
      // Lookup: valida tractor + checklist activo + recepción completada (con
      // excepción legacy). Si no pasa, mostramos el motivo y NO dejamos avanzar.
      const lookup = await produccionService.lookupParaChecklist(numSerie);
      const datos = lookup.data || lookup;

      if (!datos.puede_crear) {
        // CHECKLIST_YA_EXISTE → ofrecemos ir al detalle del existente
        if (datos.code === 'CHECKLIST_YA_EXISTE' && datos.checklist_id) {
          resultEl.innerHTML = `
            <div style="background: #fff3cd; border: 1px solid #ffc107; padding: 0.75rem; border-radius: 6px; color: #856404;">
              ${datos.mensaje}
              <div style="margin-top: 0.5rem;">
                <button class="btn btn-sm btn-primary" onclick="router.navigate('/produccion/checklists/${datos.checklist_id}')">Ver checklist existente</button>
              </div>
            </div>
          `;
        } else {
          // RECEPCION_NO_COMPLETADA, TRACTOR_NO_ENCONTRADO, etc.
          resultEl.innerHTML = `
            <div style="background: #f8d7da; border: 1px solid #f5c6cb; padding: 0.75rem; border-radius: 6px; color: #721c24;">
              ${datos.mensaje}
            </div>
          `;
        }
        this.data.tractor = null;
        document.getElementById('formularioPrincipal').style.display = 'none';
        return;
      }

      // Lookup OK → mostramos formulario
      const tractor = datos.tractor;
      this.data.tractor = tractor;
      resultEl.innerHTML = '';
      document.getElementById('formularioPrincipal').style.display = 'block';
      document.getElementById('marcaInput').value = tractor.marca || '';
      document.getElementById('modeloInput').value = tractor.modelo || '';
      this._showTabCrear('ensamble');
    } catch (error) {
      resultEl.innerHTML = `
        <div style="background: #f8d7da; border: 1px solid #f5c6cb; padding: 0.75rem; border-radius: 6px; color: #721c24;">
          ${error.message || 'Error al buscar el tractor'}
        </div>
      `;
      this.data.tractor = null;
      document.getElementById('formularioPrincipal').style.display = 'none';
    }
  },

  _getCategoriaIcon(categoria) {
    const icons = {
      'inspeccion_frontal': '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
      'inspeccion_trasera': '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg>',
      'motor': '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><path d="M6 12h4m4 0h4"/></svg>',
      'sistema_electrico': '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>',
      'engrasado': '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><circle cx="12" cy="12" r="10"/></svg>'
    };
    return icons[categoria] || '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M9 12l2 2 4-4"/></svg>';
  },

  _updateSeccionStatus(categoria) {
    const items = this.data.plantilla[categoria] || [];
    const total = items.length;
    let responded = 0;
    for (let i = 0; i < total; i++) {
      if (this._itemsEstados?.[`${categoria}_${i}`]) responded++;
    }
    const countEl = document.getElementById(`secCount-${categoria}`);
    const badgeEl = document.getElementById(`secBadge-${categoria}`);
    const iconEl = document.getElementById(`secIcon-${categoria}`);

    if (countEl) countEl.textContent = `${responded} / ${total} respondidos`;

    if (badgeEl) {
      if (responded === total) {
        badgeEl.textContent = 'Completo';
        badgeEl.style.background = 'rgba(16,185,129,0.1)';
        badgeEl.style.color = 'var(--success)';
      } else if (responded > 0) {
        badgeEl.textContent = 'En progreso';
        badgeEl.style.background = 'rgba(245,158,11,0.1)';
        badgeEl.style.color = 'var(--warning)';
      } else {
        badgeEl.textContent = 'Pendiente';
        badgeEl.style.background = 'var(--gray-100)';
        badgeEl.style.color = 'var(--gray-500)';
      }
    }

    if (iconEl) {
      if (responded === total) {
        iconEl.style.background = 'var(--success)';
        iconEl.style.color = '#fff';
      } else if (responded > 0) {
        iconEl.style.background = 'var(--warning)';
        iconEl.style.color = '#fff';
      } else {
        iconEl.style.background = 'var(--gray-200)';
        iconEl.style.color = 'var(--gray-500)';
      }
    }

    this._updateContadorGlobal();
  },

  _updateContadorGlobal() {
    const entries = Object.entries(this.data.plantilla || {});
    let total = 0;
    let responded = 0;
    for (const [categoria, items] of entries) {
      total += items.length;
      for (let i = 0; i < items.length; i++) {
        if (this._itemsEstados?.[`${categoria}_${i}`]) responded++;
      }
    }
    const counterEl = document.getElementById('itemsCounter');
    if (counterEl) counterEl.textContent = `${responded}/${total}`;
  },

  // --- Bloque acordeón: ver comentario de archivo al inicio para reglas de producto y flujo ---

  /** Saber si el cuerpo del acordeon esta visible (inline vacio o reglas CSS rompen style.display !== 'none') */
  _isSecBodyVisible(body) {
    if (!body) return false;
    return window.getComputedStyle(body).display !== 'none';
  },

  /** Cierra todos los cuerpos secBody-* del formulario nuevo salvo uno (acordeon exclusivo plantilla) */
  _cerrarOtrasSeccionesPlantillaExcepto(categoriaMantener) {
    const root = document.getElementById('formularioPrincipal') || document.getElementById('checklistContent');
    if (!root) return;
    root.querySelectorAll('[id^="secBody-"]').forEach((el) => {
      const cat = el.id.replace(/^secBody-/, '');
      if (cat === categoriaMantener) return;
      el.style.display = 'none';
      const ch = document.getElementById(`chevron-${cat}`);
      if (ch) ch.style.transform = '';
    });
  },

  _toggleSeccionPlantilla(categoria) {
    const body = document.getElementById(`secBody-${categoria}`);
    if (!body) return;
    const isOpen = this._isSecBodyVisible(body);
    const chevron = document.getElementById(`chevron-${categoria}`);
    if (isOpen) {
      body.style.display = 'none';
      if (chevron) chevron.style.transform = '';
    } else {
      this._cerrarOtrasSeccionesPlantillaExcepto(categoria);
      body.style.display = 'block';
      if (chevron) chevron.style.transform = 'rotate(180deg)';
    }
  },

  _parseItemText(itemText) {
    // Extraer modelos entre paréntesis al final: (TE254, TE354)
    const modelMatch = itemText.match(/\(([^)]+)\)\s*$/);
    const modelos = modelMatch ? modelMatch[1] : null;
    let texto = modelMatch ? itemText.replace(modelMatch[0], '').trim() : itemText;

    // Detectar valores inline con ___
    const hasInline = texto.includes('___');

    return { texto, modelos, hasInline };
  },

  /**
   * Parentesis con referencias de modelo en rojo: (TB504), (TE254, TE354), (TB504 y TD904 Cab),
   * (Serie TD), (Serie TD y TR), (TB504, TD904 cab), listas mixtas con Serie/TR1504, etc.
   */
  _highlightRefsModeloHTML(plain) {
    if (plain == null || plain === '') return '';
    const esc = String(plain)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
    return esc.replace(/\([^)]+\)/g, (full) => {
      const inner = full.slice(1, -1);
      const tieneCodigoModelo = /[A-Z]{2,4}\d{3,4}/.test(inner);
      const tieneSerie = /\bserie\b/i.test(inner);
      if (tieneCodigoModelo || tieneSerie) {
        return `<span style="color:#dc3545;font-weight:600;">${full}</span>`;
      }
      return full;
    });
  },

  _renderItemInlineText(categoria, idx, texto) {
    if (!texto.includes('___')) {
      return `<span class="item-text" style="font-size:0.85rem;color:var(--gray-700);flex:1;">${this._highlightRefsModeloHTML(texto)}</span>`;
    }

    let valorIdx = 0;
    const parts = texto.split('___');
    let html = '';
    for (let i = 0; i < parts.length; i++) {
      html += `<span style="font-size:0.85rem;color:var(--gray-700);">${this._highlightRefsModeloHTML(parts[i])}</span>`;
      if (i < parts.length - 1) {
        const vIdx = valorIdx++;
        const savedVal = this._itemsValores?.[`${categoria}_${idx}`]?.[vIdx] || '';
        html += `<input type="text" class="form-input" style="width:60px;font-size:0.8rem;padding:0.2rem 0.4rem;display:inline;text-align:center;margin:0 2px;" placeholder="___" value="${savedVal}" onchange="window.checklistDetalleView._setItemValor('${categoria}',${idx},${vIdx},this.value)" />`;
      }
    }
    return `<span class="item-text" style="font-size:0.85rem;color:var(--gray-700);flex:1;">${html}</span>`;
  },

  _setItemEstado(categoria, idx, estado, ev) {
    if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
    if (!this._itemsEstados) this._itemsEstados = {};
    const key = `${categoria}_${idx}`;
    const current = this._itemsEstados[key];

    // Toggle: si ya tiene el mismo estado, quitar
    this._itemsEstados[key] = current === estado ? null : estado;

    this._renderItemRow(categoria, idx);
    this._updateSeccionStatus(categoria);
  },

  _setItemValor(categoria, idx, valorIdx, value) {
    if (!this._itemsValores) this._itemsValores = {};
    const key = `${categoria}_${idx}`;
    if (!this._itemsValores[key]) this._itemsValores[key] = [];
    this._itemsValores[key][valorIdx] = value;
  },

  _renderItemRow(categoria, idx) {
    const row = document.getElementById(`item-${categoria}-${idx}`);
    if (!row) return;
    const key = `${categoria}_${idx}`;
    const estado = this._itemsEstados?.[key] || null;

    // Actualizar botones
    const btnC = row.querySelector('.btn-corregir');
    const btnI = row.querySelector('.btn-ideal');
    const btnNA = row.querySelector('.btn-no-aplica');
    if (btnC) {
      btnC.style.background = estado === 'corregir' ? '#dc3545' : '#fff';
      btnC.style.color = estado === 'corregir' ? '#fff' : '#dc3545';
      btnC.style.borderColor = '#dc3545';
    }
    if (btnI) {
      btnI.style.background = estado === 'ideal' ? '#198754' : '#fff';
      btnI.style.color = estado === 'ideal' ? '#fff' : '#198754';
      btnI.style.borderColor = '#198754';
    }
    if (btnNA) {
      btnNA.style.background = estado === 'no_aplica' ? '#6c757d' : '#fff';
      btnNA.style.color = estado === 'no_aplica' ? '#fff' : '#6c757d';
      btnNA.style.borderColor = '#6c757d';
    }

    // Texto tachado solo si no aplica
    const textEl = row.querySelector('.item-text');
    if (textEl) {
      textEl.style.textDecoration = estado === 'no_aplica' ? 'line-through' : 'none';
      textEl.style.color = estado === 'no_aplica' ? 'var(--gray-400)' : 'var(--gray-700)';
    }

    // Deshabilitar inputs si no aplica
    const inputs = row.querySelectorAll('input[type="text"]');
    inputs.forEach(inp => {
      inp.disabled = estado === 'no_aplica';
      inp.style.opacity = estado === 'no_aplica' ? '0.4' : '1';
      if (estado === 'no_aplica') inp.value = '';
    });
  },

  _CATEGORIAS_ENSAMBLE: ['inspeccion_frontal', 'inspeccion_trasera', 'inspeccion_plataforma', 'etiquetas', 'engrasado', 'inspeccion_filtros', 'accesorios'],
  _CATEGORIAS_FUNCIONAMIENTO: ['motor', 'tren_motriz', 'frenos', 'sistema_direccion', 'sistema_hidraulico', 'sistema_electrico', 'plataforma_operador', 'pintura_negra', 'pintura_azul', 'pintura_gris', 'engomados', 'limpieza', 'abrillantador'],

  _showTabCrear(tabName) {
    // Guardar observaciones antes de cambiar de tab
    if (this._currentTabCrear === 'observaciones') {
      const obs = document.querySelector('[name="observaciones"]');
      if (obs) this._observacionesCrear = obs.value;
    }
    this._currentTabCrear = tabName;
    const content = document.getElementById('tabContentCrear');
    if (!content) return;

    // Actualizar tabs activos
    const tabNames = ['ensamble', 'observaciones', 'funcionamiento'];
    document.querySelectorAll('.cl-tabs-bar .cl-tab').forEach((tab, i) => {
      tab.classList.toggle('active', tabNames[i] === tabName);
    });

    switch (tabName) {
      case 'ensamble':
        content.innerHTML = this._renderItemsPlantillaPorCategorias(this._CATEGORIAS_ENSAMBLE);
        break;
      case 'observaciones':
        content.innerHTML = `
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea name="observaciones" class="form-input" rows="3" placeholder="Observaciones generales...">${this._observacionesCrear || ''}</textarea>
          </div>
          ${this.renderBloquePNCPlantilla()}
        `;
        break;
      case 'funcionamiento':
        content.innerHTML = this._renderItemsPlantillaPorCategorias(this._CATEGORIAS_FUNCIONAMIENTO);
        break;
    }
  },

  _renderItemsPlantillaPorCategorias(categorias) {
    if (!this.data.plantilla) return '<p style="color: var(--text-muted);">No hay plantilla disponible</p>';

    const entries = Object.entries(this.data.plantilla).filter(([cat]) => categorias.includes(cat));
    if (entries.length === 0) return '<p style="color: var(--text-muted);">No hay items para esta sección</p>';

    if (!this._itemsEstados) this._itemsEstados = {};
    if (!this._itemsValores) this._itemsValores = {};

    const leyenda = `
      <div style="display:flex;gap:1rem;margin-bottom:0.75rem;font-size:0.75rem;color:var(--gray-500);">
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #dc3545;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#dc3545;">C</span> Corregir</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #198754;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#198754;">I</span> Ideal</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #6c757d;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#6c757d;">—</span> No aplica</span>
      </div>
    `;

    const html = leyenda + entries.map(([categoria, items]) => {
      // Calcular estado de la sección
      const respondidos = items.filter((_, idx) => this._itemsEstados?.[`${categoria}_${idx}`]).length;
      const total = items.length;
      const isComplete = respondidos === total && total > 0;
      const inProgress = respondidos > 0 && !isComplete;
      let badgeText = 'Pendiente', badgeBg = 'var(--gray-100)', badgeColor = 'var(--gray-500)';
      let iconBg = 'var(--gray-200)', iconColor = 'var(--gray-500)';
      if (isComplete) { badgeText = 'Completo'; badgeBg = 'rgba(16,185,129,0.1)'; badgeColor = 'var(--success)'; iconBg = 'var(--success)'; iconColor = 'white'; }
      else if (inProgress) { badgeText = 'En progreso'; badgeBg = 'rgba(245,158,11,0.1)'; badgeColor = '#f59e0b'; iconBg = '#f59e0b'; iconColor = 'white'; }

      return `
      <div style="border:1px solid var(--gray-200);border-radius:12px;margin-bottom:0.75rem;overflow:hidden;">
        <div style="display:flex;align-items:center;padding:0.85rem 1rem;cursor:pointer;gap:0.75rem;" onclick="window.checklistDetalleView._toggleSeccionPlantilla('${categoria}')">
          <div id="secIcon-${categoria}" style="width:32px;height:32px;border-radius:50%;background:${iconBg};color:${iconColor};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
            ${this._getCategoriaIcon(categoria)}
          </div>
          <div style="flex:1;">
            <div style="font-size:0.9rem;font-weight:600;color:var(--gray-800);text-transform:capitalize;">${categoria.replace(/_/g, ' ')}</div>
            <div id="secCount-${categoria}" style="font-size:0.75rem;color:var(--gray-500);">${respondidos} / ${total} respondidos</div>
          </div>
          <span id="secBadge-${categoria}" style="font-size:0.7rem;font-weight:600;padding:0.25rem 0.6rem;border-radius:6px;background:${badgeBg};color:${badgeColor};">${badgeText}</span>
          <span id="chevron-${categoria}" style="font-size:0.75rem;color:var(--gray-400);transition:transform 0.2s ease;display:inline-block;">&#9660;</span>
        </div>
        <div id="secBody-${categoria}" style="display:none;border-top:1px solid var(--gray-100);" onclick="event.stopPropagation()">
          ${items.map((itemText, idx) => {
            const parsed = this._parseItemText(itemText);
            const key = `${categoria}_${idx}`;
            const estado = this._itemsEstados?.[key] || null;
            const modelBadges = parsed.modelos
              ? parsed.modelos.split(',').map(m => `<span style="font-size:0.65rem;background:rgba(220,53,69,0.1);color:#dc3545;padding:0.15rem 0.5rem;border-radius:4px;font-weight:600;">${m.trim()}</span>`).join(' ')
              : '';
            // Estilos de botones según estado guardado
            const cBg = estado === 'corregir' ? '#dc3545' : '#fff';
            const cColor = estado === 'corregir' ? '#fff' : '#dc3545';
            const iBg = estado === 'ideal' ? '#198754' : '#fff';
            const iColor = estado === 'ideal' ? '#fff' : '#198754';
            const naBg = estado === 'no_aplica' ? '#6c757d' : '#fff';
            const naColor = estado === 'no_aplica' ? '#fff' : '#6c757d';
            return `
              <div id="item-${categoria}-${idx}" style="padding:0.6rem 1rem;border-bottom:1px solid var(--gray-50);" onclick="event.stopPropagation()">
                <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;">
                  <div style="display:flex;gap:4px;flex-shrink:0;">
                    <button type="button" class="btn-corregir" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'corregir',event)" style="width:28px;height:28px;border:2px solid #dc3545;border-radius:50%;background:${cBg};color:${cColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">C</button>
                    <button type="button" class="btn-ideal" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'ideal',event)" style="width:28px;height:28px;border:2px solid #198754;border-radius:50%;background:${iBg};color:${iColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">I</button>
                    <button type="button" class="btn-no-aplica" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'no_aplica',event)" style="width:28px;height:28px;border:2px solid #6c757d;border-radius:50%;background:${naBg};color:${naColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">—</button>
                  </div>
                  ${this._renderItemInlineText(categoria, idx, parsed.texto)}
                  ${modelBadges}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;}).join('');

    return html;
  },

  /** Ítems PNC solo en creación: debajo del textarea Observaciones (no van en la lista acordeón) */
  renderBloquePNCPlantilla() {
    const cat = this.PNC_CATEGORIA;
    const items = this.data.plantilla?.[cat];
    if (!items?.length) return '';

    return `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem; background: var(--bg-secondary);">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Sí se diagnostica algún producto no conforme (PNC) <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div style="display:flex;gap:1rem;margin-bottom:0.75rem;font-size:0.72rem;color:var(--gray-500);flex-wrap:wrap;">
          <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #dc3545;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#dc3545;">C</span> Corregir</span>
          <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #198754;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#198754;">I</span> Ideal</span>
          <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #6c757d;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#6c757d;">—</span> No aplica</span>
        </div>
        <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: #fff;">
          ${items.map((itemText, idx) => {
            const parsed = this._parseItemText(itemText);
            const key = `${cat}_${idx}`;
            const estado = this._itemsEstados?.[key] || null;
            const modelBadges = parsed.modelos
              ? parsed.modelos.split(',').map(m => `<span style="font-size:0.65rem;background:rgba(220,53,69,0.1);color:#dc3545;padding:0.15rem 0.5rem;border-radius:4px;font-weight:600;">${m.trim()}</span>`).join(' ')
              : '';
            const cBg = estado === 'corregir' ? '#dc3545' : '#fff';
            const cColor = estado === 'corregir' ? '#fff' : '#dc3545';
            const iBg = estado === 'ideal' ? '#198754' : '#fff';
            const iColor = estado === 'ideal' ? '#fff' : '#198754';
            const naBg = estado === 'no_aplica' ? '#6c757d' : '#fff';
            const naColor = estado === 'no_aplica' ? '#fff' : '#6c757d';
            return `
              <div id="item-${cat}-${idx}" style="padding:0.6rem 1rem;border-bottom:1px solid var(--gray-50);" onclick="event.stopPropagation()">
                <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;">
                  <div style="display:flex;gap:4px;flex-shrink:0;">
                    <button type="button" class="btn-corregir" onclick="window.checklistDetalleView._setItemEstado('${cat}',${idx},'corregir',event)" style="width:28px;height:28px;border:2px solid #dc3545;border-radius:50%;background:${cBg};color:${cColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">C</button>
                    <button type="button" class="btn-ideal" onclick="window.checklistDetalleView._setItemEstado('${cat}',${idx},'ideal',event)" style="width:28px;height:28px;border:2px solid #198754;border-radius:50%;background:${iBg};color:${iColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">I</button>
                    <button type="button" class="btn-no-aplica" onclick="window.checklistDetalleView._setItemEstado('${cat}',${idx},'no_aplica',event)" style="width:28px;height:28px;border:2px solid #6c757d;border-radius:50%;background:${naBg};color:${naColor};font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">—</button>
                  </div>
                  ${this._renderItemInlineText(cat, idx, parsed.texto)}
                  ${modelBadges}
                </div>
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  },

  renderItemsPlantilla() {
    if (!this.data.plantilla || Object.keys(this.data.plantilla).length === 0) {
      return '<p style="color: var(--text-muted);">No hay plantilla disponible</p>';
    }

    this._itemsEstados = {};
    this._itemsValores = {};

    const entries = Object.entries(this.data.plantilla).filter(([categoria]) => categoria !== this.PNC_CATEGORIA);
    const totalItems = Object.values(this.data.plantilla).reduce((sum, arr) => sum + arr.length, 0);

    return `
      <div id="itemsCounter" style="display:none;">0/${totalItems}</div>
      <div style="display:flex;gap:1rem;margin-bottom:1rem;font-size:0.75rem;color:var(--gray-500);">
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #dc3545;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#dc3545;">C</span> Corregir</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #198754;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#198754;">I</span> Ideal</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #6c757d;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#6c757d;">—</span> No aplica</span>
      </div>
      ${entries.map(([categoria, items]) => `
        <div style="border:1px solid var(--gray-200);border-radius:12px;margin-bottom:0.75rem;overflow:hidden;">
          <div style="display:flex;align-items:center;padding:0.85rem 1rem;cursor:pointer;gap:0.75rem;" onclick="window.checklistDetalleView._toggleSeccionPlantilla('${categoria}')">
            <div id="secIcon-${categoria}" style="width:32px;height:32px;border-radius:50%;background:var(--gray-200);color:var(--gray-500);display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              ${this._getCategoriaIcon(categoria)}
            </div>
            <div style="flex:1;">
              <div style="font-size:0.9rem;font-weight:600;color:var(--gray-800);text-transform:capitalize;">${categoria.replace(/_/g, ' ')}</div>
              <div id="secCount-${categoria}" style="font-size:0.75rem;color:var(--gray-500);">0 / ${items.length} respondidos</div>
            </div>
            <span id="secBadge-${categoria}" style="font-size:0.7rem;font-weight:600;padding:0.25rem 0.6rem;border-radius:6px;background:var(--gray-100);color:var(--gray-500);">Pendiente</span>
            <span id="chevron-${categoria}" style="font-size:0.75rem;color:var(--gray-400);transition:transform 0.2s ease;display:inline-block;">&#9660;</span>
          </div>
          <div id="secBody-${categoria}" style="display:none;border-top:1px solid var(--gray-100);" onclick="event.stopPropagation()">
            ${items.map((itemText, idx) => {
              const parsed = this._parseItemText(itemText);
              const modelBadges = parsed.modelos
                ? parsed.modelos.split(',').map(m => `<span style="font-size:0.65rem;background:rgba(220,53,69,0.1);color:#dc3545;padding:0.15rem 0.5rem;border-radius:4px;font-weight:600;">${m.trim()}</span>`).join(' ')
                : '';

              return `
                <div id="item-${categoria}-${idx}" style="padding:0.6rem 1rem;border-bottom:1px solid var(--gray-50);" onclick="event.stopPropagation()">
                  <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;">
                    <div style="display:flex;gap:4px;flex-shrink:0;">
                      <button type="button" class="btn-corregir" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'corregir',event)" style="width:28px;height:28px;border:2px solid #dc3545;border-radius:50%;background:#fff;color:#dc3545;font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">C</button>
                      <button type="button" class="btn-ideal" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'ideal',event)" style="width:28px;height:28px;border:2px solid #198754;border-radius:50%;background:#fff;color:#198754;font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">I</button>
                      <button type="button" class="btn-no-aplica" onclick="window.checklistDetalleView._setItemEstado('${categoria}',${idx},'no_aplica',event)" style="width:28px;height:28px;border:2px solid #6c757d;border-radius:50%;background:#fff;color:#6c757d;font-size:0.7rem;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;">—</button>
                    </div>
                    ${this._renderItemInlineText(categoria, idx, parsed.texto)}
                    ${modelBadges}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      `).join('')}
    `;
  },

  renderChecklistExistente() {
    const container = document.getElementById('checklistContent');
    const c = this.data.checklist;

    // Categorías por tab
    const categoriasEnsamble = ['inspeccion_frontal', 'inspeccion_trasera', 'inspeccion_plataforma', 'etiquetas', 'engrasado', 'inspeccion_filtros', 'accesorios'];
    const categoriasFuncionamiento = ['motor', 'tren_motriz', 'frenos', 'sistema_direccion', 'sistema_hidraulico', 'sistema_electrico', 'plataforma_operador', 'pintura_negra', 'pintura_azul', 'pintura_gris', 'engomados', 'limpieza', 'abrillantador'];

    const filterItems = (cats) => {
      const filtered = {};
      for (const cat of cats) {
        if (c.items_por_categoria?.[cat]) filtered[cat] = c.items_por_categoria[cat];
      }
      return filtered;
    };

    this._currentTabCL = 'ensamble';

    const fp = (c.fecha || '').substring(0, 10).split('-');
    const fechaStr = fp.length === 3 ? `${fp[2]}/${fp[1]}/${fp[0]}` : '-';

    container.innerHTML = `
      ${this._renderBannerPNC()}
      <!-- Encabezado con info del tractor -->
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1.5rem;">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Datos del Tractor <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Num. Serie</label>
            <input type="text" class="form-input" value="${c.num_serie}" readonly style="background: var(--gray-50);" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Modelo</label>
            <input type="text" class="form-input" value="${c.modelo}" readonly style="background: var(--gray-50);" />
          </div>
        </div>
        <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Marca</label>
            <input type="text" class="form-input" value="${c.marca || '-'}" readonly style="background: var(--gray-50);" />
          </div>
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Fecha</label>
            <input type="text" class="form-input" value="${fechaStr}" readonly style="background: var(--gray-50);" />
          </div>
        </div>
        <div class="ot-grid-2col" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem; margin-top: 0.5rem;">
          <div>
            <label style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 0.5px; color: var(--gray-500); margin-bottom: 0.25rem; display: block;">Horas Motor</label>
            <input type="text" class="form-input" value="${c.horas_motor || 0} hrs" readonly style="background: var(--gray-50);" />
          </div>
        </div>
      </div>

      <!-- Tabs -->
      <div class="cl-tabs-bar">
        <button type="button" class="cl-tab active" onclick="window.checklistDetalleView._showTabCL('ensamble')">Ensamble</button>
        <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCL('torque')">Diagrama/Torque</button>
        <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCL('niveles')">Niveles</button>
        <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCL('funcionamiento')">Funcionamiento</button>
        <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCL('observaciones')">Observaciones</button>
        <button type="button" class="cl-tab" onclick="window.checklistDetalleView._showTabCL('firmas')">Firmas</button>
      </div>

      <!-- Contenido de tabs -->
      <div id="tabContentCL"></div>

      <!-- Indicador de cambios sin guardar -->
      <div id="unsavedIndicator" style="display: none; background: var(--warning); color: white; padding: 0.5rem 1rem; border-radius: 8px; margin-bottom: 1rem; text-align: center;">
        Hay cambios sin guardar
      </div>

      <!-- Botones de acción -->
      ${!c.completado ? `
        <div style="display: flex; gap: 1rem; justify-content: flex-end; flex-wrap: wrap; margin-top: 1rem;">
          <button class="btn btn-primary" onclick="window.checklistDetalleView.guardarCambios()">
            Guardar Cambios
          </button>
          ${permisos.can('checklists', 'cerrar') ? `
            <button class="btn btn-success" onclick="window.checklistDetalleView.completarChecklist()">
              Completar Check List
            </button>
          ` : ''}
        </div>
      ` : ''}
    `;

    // Guardar categorías filtradas para uso en tabs
    this._categoriasEnsamble = categoriasEnsamble;
    this._categoriasFuncionamiento = categoriasFuncionamiento;

    // Mostrar primer tab
    this._showTabCL('ensamble');

  },

  renderItemsExistentes(itemsPorCategoria) {
    if (!itemsPorCategoria || Object.keys(itemsPorCategoria).length === 0) {
      return `
        <div style="text-align: center; padding: 2rem; color: var(--text-muted);">
          No hay items registrados.
          ${!this.data.isReadOnly ? `
            <br><br>
            <button class="btn btn-primary" onclick="window.checklistDetalleView.agregarItemsPlantilla()">
              Agregar Items de Plantilla
            </button>
          ` : ''}
        </div>
      `;
    }

    const entries = Object.entries(itemsPorCategoria);
    const totalItems = entries.reduce((sum, [, items]) => sum + items.length, 0);
    const totalRespondidos = entries.reduce((sum, [, items]) => sum + items.filter(i => i.estado).length, 0);
    const isReadOnly = this.data.isReadOnly;

    return `
      <div style="display:flex;gap:1rem;margin-bottom:1rem;font-size:0.75rem;color:var(--gray-500);">
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #dc3545;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#dc3545;">C</span> Corregir</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #198754;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#198754;">I</span> Ideal</span>
        <span style="display:flex;align-items:center;gap:0.3rem;"><span style="width:18px;height:18px;border-radius:50%;border:2px solid #6c757d;display:inline-flex;align-items:center;justify-content:center;font-size:0.6rem;font-weight:700;color:#6c757d;">—</span> No aplica</span>
      </div>
      ${entries.map(([categoria, items]) => {
        const responded = items.filter(i => i.estado).length;
        const total = items.length;

        let badgeText = 'Pendiente', badgeBg = 'var(--gray-100)', badgeColor = 'var(--gray-500)';
        if (responded === total) {
          badgeText = 'Completo'; badgeBg = 'rgba(16,185,129,0.1)'; badgeColor = 'var(--success)';
        } else if (responded > 0) {
          badgeText = 'En progreso'; badgeBg = 'rgba(245,158,11,0.1)'; badgeColor = 'var(--warning)';
        }

        let iconBg = 'var(--gray-200)', iconColor = 'var(--gray-500)';
        if (responded === total) {
          iconBg = 'var(--success)'; iconColor = '#fff';
        } else if (responded > 0) {
          iconBg = 'var(--warning)'; iconColor = '#fff';
        }

        return `
          <div style="border:1px solid var(--gray-200);border-radius:12px;margin-bottom:0.75rem;overflow:hidden;">
            <div style="display:flex;align-items:center;padding:0.85rem 1rem;cursor:pointer;gap:0.75rem;" onclick="window.checklistDetalleView._toggleSeccionExistente('${categoria}')">
              <div style="width:32px;height:32px;border-radius:50%;background:${iconBg};color:${iconColor};display:flex;align-items:center;justify-content:center;flex-shrink:0;">
                ${this._getCategoriaIcon(categoria)}
              </div>
              <div style="flex:1;">
                <div style="font-size:0.9rem;font-weight:600;color:var(--gray-800);text-transform:capitalize;">${categoria.replace(/_/g, ' ')}</div>
                <div style="font-size:0.75rem;color:var(--gray-500);">${responded} / ${total} respondidos</div>
              </div>
              <span style="font-size:0.7rem;font-weight:600;padding:0.25rem 0.6rem;border-radius:6px;background:${badgeBg};color:${badgeColor};">${badgeText}</span>
              <span id="chevronEx-${categoria}" style="font-size:0.75rem;color:var(--gray-400);transition:transform 0.2s ease;display:inline-block;">&#9660;</span>
            </div>
            <div id="secBodyEx-${categoria}" style="display:none;border-top:1px solid var(--gray-100);" onclick="event.stopPropagation()">
              ${items.map(item => {
                const parsed = this._parseItemText(item.item);
                const modelBadges = parsed.modelos
                  ? parsed.modelos.split(',').map(m => `<span style="font-size:0.65rem;background:rgba(220,53,69,0.1);color:#dc3545;padding:0.15rem 0.5rem;border-radius:4px;font-weight:600;">${m.trim()}</span>`).join(' ')
                  : '';
                const isNoAplica = item.estado === 'no_aplica';
                const textStyle = isNoAplica ? 'text-decoration:line-through;color:var(--gray-400);' : 'color:var(--gray-700);';

                // Render inline text con inputs
                let inlineHtml;
                if (parsed.hasInline && !isReadOnly) {
                  let vIdx = 0;
                  const valores = this.parseValoresItem(item.valores_inline);
                  const parts = parsed.texto.split('___');
                  inlineHtml = parts
                    .map((seg, pi) => {
                      const h = this._highlightRefsModeloHTML(seg);
                      if (pi === parts.length - 1) return h;
                      const val = valores[vIdx] || '';
                      const currentIdx = vIdx++;
                      return `${h}<input type="text" class="form-input" style="width:60px;font-size:0.8rem;padding:0.2rem 0.4rem;display:inline;text-align:center;margin:0 2px;" value="${val}" ${isNoAplica ? 'disabled style="opacity:0.4;width:60px;font-size:0.8rem;padding:0.2rem 0.4rem;display:inline;text-align:center;margin:0 2px;"' : ''} onchange="window.checklistDetalleView.guardarValorItem(${item.id},${currentIdx},this.value)" />`;
                    })
                    .join('');
                } else if (parsed.hasInline && isReadOnly) {
                  const valores = this.parseValoresItem(item.valores_inline);
                  let vIdx = 0;
                  const parts = parsed.texto.split('___');
                  inlineHtml = parts
                    .map((seg, pi) => {
                      const h = this._highlightRefsModeloHTML(seg);
                      if (pi === parts.length - 1) return h;
                      const val = valores[vIdx++] || '___';
                      return `${h}<strong>${val}</strong>`;
                    })
                    .join('');
                } else {
                  inlineHtml = this._highlightRefsModeloHTML(parsed.texto);
                }

                return `
                  <div style="padding:0.6rem 1rem;border-bottom:1px solid var(--gray-50);" onclick="event.stopPropagation()">
                    <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;">
                      <div style="display:flex;gap:4px;flex-shrink:0;">
                        <button type="button" style="width:28px;height:28px;border:2px solid #dc3545;border-radius:50%;background:${item.estado === 'corregir' ? '#dc3545' : '#fff'};color:${item.estado === 'corregir' ? '#fff' : '#dc3545'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'corregir' ? '' : 'corregir'}',event)"`}>C</button>
                        <button type="button" style="width:28px;height:28px;border:2px solid #198754;border-radius:50%;background:${item.estado === 'ideal' ? '#198754' : '#fff'};color:${item.estado === 'ideal' ? '#fff' : '#198754'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'ideal' ? '' : 'ideal'}',event)"`}>I</button>
                        <button type="button" style="width:28px;height:28px;border:2px solid #6c757d;border-radius:50%;background:${item.estado === 'no_aplica' ? '#6c757d' : '#fff'};color:${item.estado === 'no_aplica' ? '#fff' : '#6c757d'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'no_aplica' ? '' : 'no_aplica'}',event)"`}>—</button>
                      </div>
                      <span class="item-text" style="font-size:0.85rem;${textStyle};flex:1;">${inlineHtml}</span>
                      ${modelBadges}
                      ${!isReadOnly ? `
                        <button type="button" class="btn btn-sm" style="border:1px solid var(--gray-200);background:#fff;color:var(--gray-500);font-size:0.7rem;padding:0.2rem 0.5rem;" onclick="event.stopPropagation();window.checklistDetalleView.editarObservacionItem(${item.id},'${(item.observacion || '').replace(/'/g, "\\'")}')" title="Observación">Obs</button>
                      ` : ''}
                    </div>
                    ${item.observacion ? `<div style="margin:0.35rem 0 0 6rem;font-size:0.75rem;color:var(--gray-500);font-style:italic;border-left:2px solid var(--primary);padding-left:0.5rem;">${item.observacion}</div>` : ''}
                  </div>
                `;
              }).join('')}
            </div>
          </div>
        `;
      }).join('')}
    `;
  },

  /** Fila PNC en edicion: C / I / — sin boton Obs (las notas van en el textarea general del tab) */
  _renderFilaItemExistentePNC(item, isReadOnly) {
    const parsed = this._parseItemText(item.item);
    const modelBadges = parsed.modelos
      ? parsed.modelos.split(',').map(m => `<span style="font-size:0.65rem;background:rgba(220,53,69,0.1);color:#dc3545;padding:0.15rem 0.5rem;border-radius:4px;font-weight:600;">${m.trim()}</span>`).join(' ')
      : '';
    const isNoAplica = item.estado === 'no_aplica';
    const textStyle = isNoAplica ? 'text-decoration:line-through;color:var(--gray-400);' : 'color:var(--gray-700);';

    let inlineHtml;
    if (parsed.hasInline && !isReadOnly) {
      let vIdx = 0;
      const valores = this.parseValoresItem(item.valores_inline);
      const parts = parsed.texto.split('___');
      inlineHtml = parts
        .map((seg, pi) => {
          const h = this._highlightRefsModeloHTML(seg);
          if (pi === parts.length - 1) return h;
          const val = valores[vIdx] || '';
          const currentIdx = vIdx++;
          return `${h}<input type="text" class="form-input" style="width:60px;font-size:0.8rem;padding:0.2rem 0.4rem;display:inline;text-align:center;margin:0 2px;" value="${val}" ${isNoAplica ? 'disabled style="opacity:0.4;width:60px;font-size:0.8rem;padding:0.2rem 0.4rem;display:inline;text-align:center;margin:0 2px;"' : ''} onchange="window.checklistDetalleView.guardarValorItem(${item.id},${currentIdx},this.value)" />`;
        })
        .join('');
    } else if (parsed.hasInline && isReadOnly) {
      const valores = this.parseValoresItem(item.valores_inline);
      let vIdx = 0;
      const parts = parsed.texto.split('___');
      inlineHtml = parts
        .map((seg, pi) => {
          const h = this._highlightRefsModeloHTML(seg);
          if (pi === parts.length - 1) return h;
          const val = valores[vIdx++] || '___';
          return `${h}<strong>${val}</strong>`;
        })
        .join('');
    } else {
      inlineHtml = this._highlightRefsModeloHTML(parsed.texto);
    }

    return `
      <div style="padding:0.6rem 1rem;border-bottom:1px solid var(--gray-50);" onclick="event.stopPropagation()">
        <div style="display:flex;align-items:center;gap:0.5rem;flex-wrap:wrap;">
          <div style="display:flex;gap:4px;flex-shrink:0;">
            <button type="button" style="width:28px;height:28px;border:2px solid #dc3545;border-radius:50%;background:${item.estado === 'corregir' ? '#dc3545' : '#fff'};color:${item.estado === 'corregir' ? '#fff' : '#dc3545'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'corregir' ? '' : 'corregir'}',event)"`}>C</button>
            <button type="button" style="width:28px;height:28px;border:2px solid #198754;border-radius:50%;background:${item.estado === 'ideal' ? '#198754' : '#fff'};color:${item.estado === 'ideal' ? '#fff' : '#198754'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'ideal' ? '' : 'ideal'}',event)"`}>I</button>
            <button type="button" style="width:28px;height:28px;border:2px solid #6c757d;border-radius:50%;background:${item.estado === 'no_aplica' ? '#6c757d' : '#fff'};color:${item.estado === 'no_aplica' ? '#fff' : '#6c757d'};font-size:0.7rem;font-weight:700;cursor:${isReadOnly ? 'default' : 'pointer'};display:flex;align-items:center;justify-content:center;" ${isReadOnly ? 'disabled' : `onclick="window.checklistDetalleView.cambiarEstadoItem(${item.id},'${item.estado === 'no_aplica' ? '' : 'no_aplica'}',event)"`}>—</button>
          </div>
          <span class="item-text" style="font-size:0.85rem;${textStyle};flex:1;">${inlineHtml}</span>
          ${modelBadges}
        </div>
      </div>
    `;
  },

  /** Bloque PNC bajo el textarea en tab Observaciones (edición/lectura) */
  renderSeccionPNCObservaciones(c) {
    const cat = this.PNC_CATEGORIA;
    const items = c.items_por_categoria?.[cat];
    const isReadOnly = this.data.isReadOnly;
    const puedeAgregar = !c.completado && !isReadOnly;

    let cuerpo = '';
    if (items?.length) {
      cuerpo = `
        <div style="display:flex;gap:1rem;margin-bottom:0.75rem;font-size:0.72rem;color:var(--gray-500);flex-wrap:wrap;">
          <span style="display:flex;align-items:center;gap:0.25rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #dc3545;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#dc3545;">C</span> Corregir</span>
          <span style="display:flex;align-items:center;gap:0.25rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #198754;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#198754;">I</span> Ideal</span>
          <span style="display:flex;align-items:center;gap:0.25rem;"><span style="width:16px;height:16px;border-radius:50%;border:2px solid #6c757d;display:inline-flex;align-items:center;justify-content:center;font-size:0.55rem;font-weight:700;color:#6c757d;">—</span> No aplica</span>
        </div>
        <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: #fff;">
          ${items.map(item => this._renderFilaItemExistentePNC(item, isReadOnly)).join('')}
        </div>
      `;
    } else if (puedeAgregar) {
      cuerpo = `
        <p style="margin:0 0 0.75rem;font-size:0.8rem;color:var(--gray-600);">Aún no hay ítems PNC en este checklist.</p>
        <button type="button" class="btn btn-secondary btn-sm" onclick="window.checklistDetalleView.agregarItemsPNCDesdePlantilla()">Agregar ítems PNC desde plantilla</button>
      `;
    } else {
      cuerpo = '<p style="margin:0;font-size:0.8rem;color:var(--text-muted);">Sin ítems PNC registrados.</p>';
    }

    return `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-top: 1rem; background: var(--bg-secondary);">
        <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Sí se diagnostica algún producto no conforme (PNC) <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
        ${cuerpo}
      </div>
    `;
  },

  /** Cierra otras secciones del tab Ensamble/Funcionamiento; solo una expandida a la vez */
  _cerrarOtrasSeccionesExistenteExcepto(categoriaMantener) {
    const root = document.getElementById('tabContentCL');
    if (!root) return;
    if (!this._seccionesAbiertasEx) this._seccionesAbiertasEx = {};
    root.querySelectorAll('[id^="secBodyEx-"]').forEach((el) => {
      const cat = el.id.replace(/^secBodyEx-/, '');
      if (cat === categoriaMantener) return;
      el.style.display = 'none';
      const ch = document.getElementById(`chevronEx-${cat}`);
      if (ch) ch.style.transform = '';
      this._seccionesAbiertasEx[cat] = false;
    });
  },

  _toggleSeccionExistente(categoria) {
    const body = document.getElementById(`secBodyEx-${categoria}`);
    if (!body) return;
    const isOpen = this._isSecBodyVisible(body);
    const chevron = document.getElementById(`chevronEx-${categoria}`);
    if (!this._seccionesAbiertasEx) this._seccionesAbiertasEx = {};

    if (isOpen) {
      body.style.display = 'none';
      if (chevron) chevron.style.transform = '';
      this._seccionesAbiertasEx[categoria] = false;
    } else {
      this._cerrarOtrasSeccionesExistenteExcepto(categoria);
      body.style.display = 'block';
      if (chevron) chevron.style.transform = 'rotate(180deg)';
      this._seccionesAbiertasEx[categoria] = true;
    }
  },

  _restaurarSeccionesAbiertas() {
    if (!this._seccionesAbiertasEx) return;
    // Una sola seccion abierta: si el mapa tenia varias en true, quedarse con la primera
    const abierta = Object.entries(this._seccionesAbiertasEx).find(([, open]) => open)?.[0];
    const root = document.getElementById('tabContentCL');
    if (root) {
      root.querySelectorAll('[id^="secBodyEx-"]').forEach((el) => {
        el.style.display = 'none';
        const cat = el.id.replace(/^secBodyEx-/, '');
        const ch = document.getElementById(`chevronEx-${cat}`);
        if (ch) ch.style.transform = '';
      });
    }
    this._seccionesAbiertasEx = abierta ? { [abierta]: true } : {};

    if (abierta) {
      const body = document.getElementById(`secBodyEx-${abierta}`);
      if (body) body.style.display = 'block';
      const chevron = document.getElementById(`chevronEx-${abierta}`);
      if (chevron) chevron.style.transform = 'rotate(180deg)';
    }
  },

  /** Antes de innerHTML del mismo tab, alinear mapa con lo que el usuario ve (evita reabrir seccion anterior al marcar item o cambiar de seccion) */
  _syncSeccionesAbiertasExDesdeDom(categorias) {
    if (!categorias?.length) return;
    if (!this._seccionesAbiertasEx) this._seccionesAbiertasEx = {};
    let primeraVisible = null;
    for (const cat of categorias) {
      const body = document.getElementById(`secBodyEx-${cat}`);
      if (!body) continue;
      const vis = this._isSecBodyVisible(body);
      this._seccionesAbiertasEx[cat] = vis;
      if (vis && !primeraVisible) primeraVisible = cat;
    }
    // Acordeon exclusivo: no mas de una entrada true en el mapa
    if (primeraVisible) {
      for (const cat of categorias) {
        this._seccionesAbiertasEx[cat] = cat === primeraVisible;
      }
    }
  },

  _showTabCL(tabName) {
    const tabAnterior = this._currentTabCL;
    const reRenderItemsEnsamble =
      tabName === 'ensamble' && tabAnterior === 'ensamble';
    const reRenderItemsFuncionamiento =
      tabName === 'funcionamiento' && tabAnterior === 'funcionamiento';

    // Limpiar estado de secciones al cambiar de tab (no al re-renderizar mismo tab)
    if (tabName !== tabAnterior) {
      this._seccionesAbiertasEx = {};
    }
    this._currentTabCL = tabName;
    const content = document.getElementById('tabContentCL');
    if (!content) return;

    // Actualizar tabs activos
    document.querySelectorAll('.cl-tabs-bar .cl-tab').forEach((tab, i) => {
      const tabs = ['ensamble', 'torque', 'niveles', 'funcionamiento', 'observaciones', 'firmas'];
      tab.classList.toggle('active', tabs[i] === tabName);
    });

    const c = this.data.checklist;
    const filterItems = (cats) => {
      const filtered = {};
      for (const cat of cats) {
        if (c.items_por_categoria?.[cat]) filtered[cat] = c.items_por_categoria[cat];
      }
      return filtered;
    };

    switch (tabName) {
      case 'ensamble':
        if (reRenderItemsEnsamble) {
          this._syncSeccionesAbiertasExDesdeDom(this._categoriasEnsamble);
        }
        content.innerHTML = this.renderItemsExistentes(filterItems(this._categoriasEnsamble));
        break;
      case 'torque':
        content.innerHTML = this.renderTorques(c);
        break;
      case 'niveles':
        content.innerHTML = this.renderNiveles(c);
        break;
      case 'funcionamiento':
        if (reRenderItemsFuncionamiento) {
          this._syncSeccionesAbiertasExDesdeDom(this._categoriasFuncionamiento);
        }
        content.innerHTML = this.renderItemsExistentes(filterItems(this._categoriasFuncionamiento));
        break;
      case 'observaciones': {
        // Texto libre arriba; PNC (producto_no_conforme) debajo, alineado al formato impreso
        content.innerHTML = `
          <div style="border: 1px solid var(--gray-200); border-radius: 8px; padding: 1.25rem; margin-bottom: 1rem;">
            <h4 style="margin: 0 0 1rem; font-size: 0.8rem; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); display: flex; align-items: center; gap: 0.75rem;">Observaciones <span style="flex: 1; height: 2px; background: var(--gray-200);"></span></h4>
            <textarea id="observaciones" class="form-input" rows="5" ${this.data.isReadOnly ? 'readonly style="background:var(--gray-50);"' : 'placeholder="Observaciones generales..."'}>${c.observaciones || ''}</textarea>
          </div>
          ${this.renderSeccionPNCObservaciones(c)}
        `;
        if (!this.data.isReadOnly) {
          const obsEl = document.getElementById('observaciones');
          if (obsEl) obsEl.addEventListener('input', () => this._showUnsavedCL());
        }
        break;
      }
      case 'firmas':
        content.innerHTML = !c.completado ? this.renderSeccionFirmas() : this.renderFirmasExistentes(c);
        if (!c.completado) setTimeout(() => this.loadSignaturePad(), 100);
        break;
    }
  },

  _showUnsavedCL() {
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
  },

  _renderFirmaCLEdicion(tipo, label, nombre, existingUrl) {
    const cuerpo = existingUrl ? `
        <div style="padding: 1.5rem; text-align: center;">
          <img src="${existingUrl}" style="width: 100%; max-height: 100px; object-fit: contain;" onerror="this.style.display='none'" />
        </div>
        <div style="padding: 0 1rem 0.25rem; text-align: center;">
          <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.checklistDetalleView.cambiarFirmaExistenteCL('${tipo}')">Cambiar firma</a>
        </div>
      ` : `
        <div class="firma-box" style="position: relative;">
          <canvas id="firma-${tipo}" class="firma-canvas-new"></canvas>
          <span class="firma-placeholder" id="firma-placeholder-${tipo}" style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align: center; pointer-events: none; color: var(--gray-400);">
            <span style="font-size: 0.8rem;">Firme aquí</span>
          </span>
        </div>
        <div style="padding: 0 1rem 0.25rem; text-align: center;">
          <a href="javascript:void(0)" style="font-size: 0.8rem; color: var(--primary);" onclick="window.checklistDetalleView.limpiarFirmaCompletar('${tipo}')">Limpiar</a>
        </div>
      `;

    return `
      <div style="border: 1px solid var(--gray-200); border-radius: 8px; overflow: hidden; background: white;">
        <div style="background: var(--bg-secondary); padding: 0.6rem; text-align: center; border-bottom: 1px solid var(--gray-200);">
          <span style="font-size: 0.7rem; text-transform: uppercase; letter-spacing: 1px; color: var(--primary); font-weight: 600;">${label} *</span>
        </div>
        ${cuerpo}
        <div style="padding: 0.5rem 1rem 0.75rem; border-top: 1px solid var(--gray-200);">
          <span style="font-size: 0.7rem; color: var(--gray-500); text-transform: uppercase; letter-spacing: 0.5px;">Nombre</span>
          <p style="margin: 0.15rem 0 0; font-weight: 600; font-size: 0.85rem;">${nombre}</p>
        </div>
      </div>
    `;
  },

  cambiarFirmaExistenteCL(tipo) {
    const c = this.data.checklist;
    if (!c) return;
    if (tipo === 'tecnico') c.tecnico_ensamble_firma_url = null;
    if (tipo === 'linea') c.responsable_linea_firma_url = null;
    if (tipo === 'area') c.responsable_area_firma_url = null;
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator) indicator.style.display = 'block';
    this.renderChecklistExistente();
    // Volver al tab firmas tras el re-render
    setTimeout(() => {
      this._showTabCL('firmas');
      this.loadSignaturePad();
    }, 100);
  },

  renderSeccionFirmas() {
    const c = this.data.checklist;
    const tecnicoNombre = c.tecnico_ensamble_nombre || c.tecnico_ensamble || '-';
    const lineaNombre = c.responsable_linea_nombre || c.responsable_linea || '-';
    const areaNombre = c.responsable_area_nombre || c.responsable_area || '-';

    return `
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <div id="firmasContainer" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="ot-grid-firmas">
          ${this._renderFirmaCLEdicion('tecnico', 'Técnico de Ensamble', tecnicoNombre, c.tecnico_ensamble_firma_url)}
          ${this._renderFirmaCLEdicion('linea', 'Resp. Línea de Producto', lineaNombre, c.responsable_linea_firma_url)}
          ${this._renderFirmaCLEdicion('area', 'Resp. Área de Producción', areaNombre, c.responsable_area_firma_url)}
        </div>
      </div>
    `;
  },

  // Misma presentacion que OrdenTrabajoDetalle._renderFirmaLectura (imagen, linea negra, nombre, rol)
  _renderFirmaCLLectura(label, nombre, url) {
    return `
      <div style="text-align: center; padding: 0.5rem;">
        <div style="min-height: 150px; display: flex; align-items: center; justify-content: center;">
          ${url
            ? `<img src="${url}" style="width: 100%; max-height: 150px; object-fit: contain;" onerror="this.style.display='none'" />`
            : '<span style="color: var(--text-secondary);">Sin firma</span>'
          }
        </div>
        <hr style="border: none; border-top: 3px solid #000; margin: 0.5rem 0;" />
        <p style="margin: 0.25rem 0 0; font-weight: 600; font-size: 0.85rem;">${nombre || '-'}</p>
        <p style="margin: 0.25rem 0 0; color: var(--text-secondary); font-size: 0.8rem;">${label}</p>
      </div>
    `;
  },

  renderFirmasExistentes(c) {
    const tecnicoNombre = c.tecnico_ensamble_nombre || c.tecnico_ensamble || '-';
    const lineaNombre = c.responsable_linea_nombre || c.responsable_linea || '-';
    const areaNombre = c.responsable_area_nombre || c.responsable_area || '-';

    return `
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px;">
        <div id="firmasContainer" style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 1.5rem;" class="ot-grid-firmas">
          ${this._renderFirmaCLLectura('Técnico de Ensamble', tecnicoNombre, c.tecnico_ensamble_firma_url)}
          ${this._renderFirmaCLLectura('Resp. Línea de Producto', lineaNombre, c.responsable_linea_firma_url)}
          ${this._renderFirmaCLLectura('Resp. Área de Producción', areaNombre, c.responsable_area_firma_url)}
        </div>
      </div>
    `;
  },

  async buscarTractor(numSerie) {
    if (!numSerie) return;

    const infoEl = document.getElementById('tractorInfo');
    infoEl.textContent = 'Buscando tractor...';

    try {
      const response = await tractorService.getByNumSerie(numSerie);
      const tractor = response.data || response;

      if (tractor) {
        this.data.tractor = tractor;
        infoEl.innerHTML = `<span style="color: var(--success);">Tractor encontrado: ${tractor.modelo} (${tractor.marca})</span>`;
      }
    } catch (error) {
      infoEl.innerHTML = `<span style="color: var(--danger);">Tractor no encontrado</span>`;
      this.data.tractor = null;
    }
  },

  async guardarNuevo(e) {
    e.preventDefault();

    const form = e.target;
    const formData = new FormData(form);

    // Validar que se haya buscado el tractor primero
    if (!this.data.tractor) {
      toast.error('Debe buscar y seleccionar un tractor válido primero');
      return;
    }

    // Obtener responsables de los selects
    const tecnicoSelect = form.querySelector('select[name="tecnico_ensamble_id"]');
    const lineaSelect = form.querySelector('select[name="responsable_linea_id"]');
    const areaSelect = form.querySelector('select[name="responsable_area_id"]');

    const tecnicoId = tecnicoSelect?.value || null;
    const tecnicoNombre = tecnicoSelect?.options[tecnicoSelect.selectedIndex]?.text?.trim() || null;
    const lineaId = lineaSelect?.value || null;
    const lineaNombre = lineaSelect?.options[lineaSelect.selectedIndex]?.text?.trim() || null;
    const areaId = areaSelect?.value || null;
    const areaNombre = areaSelect?.options[areaSelect.selectedIndex]?.text?.trim() || null;

    // Validaciones
    if (!this.data.tractor) {
      toast.error('Debe buscar y seleccionar un tractor válido primero');
      return;
    }


    if (!tecnicoId) {
      toast.error('El Técnico de Producción es requerido');
      return;
    }

    if (!lineaId) {
      toast.error('El Responsable de Línea de Producto es requerido');
      return;
    }

    if (!areaId) {
      toast.error('El Responsable de Área de Producción es requerido');
      return;
    }

    const data = {
      num_serie: this.data.tractor.num_serie,
      fecha: formData.get('fecha'),
      modelo: this.data.tractor.modelo,
      horas_motor: parseFloat(formData.get('horas_motor')) || 0,
      tecnico_ensamble: tecnicoNombre,
      tecnico_ensamble_id: parseInt(tecnicoId, 10),
      responsable_linea: lineaNombre,
      responsable_linea_id: parseInt(lineaId, 10),
      responsable_area: areaNombre,
      responsable_area_id: parseInt(areaId, 10),
      observaciones: formData.get('observaciones') || this._observacionesCrear || null
    };

    // Enviar todos los ítems con sus estados
    const items = [];
    for (const [categoria, catItems] of Object.entries(this.data.plantilla || {})) {
      catItems.forEach((itemText, idx) => {
        const key = `${categoria}_${idx}`;
        const estado = this._itemsEstados?.[key] || null;
        const valores = this._itemsValores?.[key] || null;
        items.push({
          categoria,
          item: itemText,
          estado,
          verificado: !!estado,
          valores_inline: valores && valores.some(v => v) ? valores : null
        });
      });
    }
    data.items = items;

    try {
      this._showProgress('Creando check list...');
      const response = await produccionService.crearChecklist(data);
      this._hideProgress();
      toast.success('Check list creado exitosamente');
      // Limpiar el estado de captura para que no se arrastre al siguiente checklist nuevo.
      this._resetEstadoCreacion();
      const checklist = response.data || response;
      router.navigate(`/produccion/checklists/${checklist.id}`);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al crear check list');
    }
  },

  cambiarEstadoItem(itemId, estado, ev) {
    if (ev && typeof ev.stopPropagation === 'function') ev.stopPropagation();
    // Actualizar estado localmente
    const item = this.data.checklist.items.find(i => i.id === itemId);
    if (!item) return;

    item.estado = estado || null;

    // Re-agrupar por categoría
    this.data.checklist.items_por_categoria = this.data.checklist.items.reduce((acc, i) => {
      if (!acc[i.categoria]) acc[i.categoria] = [];
      acc[i.categoria].push(i);
      return acc;
    }, {});

    // Re-renderizar tab actual y mostrar indicador
    this._showTabCL(this._currentTabCL || 'ensamble');
    if (this._currentTabCL !== 'observaciones') {
      this._restaurarSeccionesAbiertas();
    }
    this._showUnsaved();
  },

  editarObservacionItem(itemId, observacionActual) {
    modal.form(
      'Observación del Item',
      `
        <div class="form-group">
          <label class="form-label">Observación</label>
          <textarea
            id="modalObservacion"
            class="form-input"
            rows="3"
          >${observacionActual}</textarea>
        </div>
      `,
      () => {
        const observacion = document.getElementById('modalObservacion').value;
        const item = this.data.checklist.items.find(i => i.id === itemId);
        if (item) {
          item.observacion = observacion;
          // Re-renderizar tab actual para mostrar la observación actualizada
          this.data.checklist.items_por_categoria = this.data.checklist.items.reduce((acc, i) => {
            if (!acc[i.categoria]) acc[i.categoria] = [];
            acc[i.categoria].push(i);
            return acc;
          }, {});
          this._showTabCL(this._currentTabCL || 'ensamble');
          if (this._currentTabCL !== 'observaciones') {
            this._restaurarSeccionesAbiertas();
          }
        }
        this._showUnsaved();
        modal.close();
      }
    );
  },

  async guardarCambios() {
    const observacionesEl = document.getElementById('observaciones');
    const observaciones = observacionesEl ? observacionesEl.value : (this.data.checklist.observaciones || '');
    const c = this.data.checklist;

    try {
      this._showProgress('Preparando datos...');

      // Subir firmas dibujadas (si hay) al bucket unificado checklist/{num_serie}/firmas/
      let firmaTecnicoUrl = c.tecnico_ensamble_firma_url || null;
      let firmaLineaUrl = c.responsable_linea_firma_url || null;
      let firmaAreaUrl = c.responsable_area_firma_url || null;

      // Solo subir si el pad tiene trazo Y aún no hay URL persistida (red de seguridad)
      if (this.signaturePadTecnico && !this.signaturePadTecnico.isEmpty() && !firmaTecnicoUrl) {
        this._showProgress('Subiendo firma del técnico...');
        firmaTecnicoUrl = await recepcionService.subirFirma(
          await this.toBlob(this.signaturePadTecnico),
          `CL-${c.id}_firma_tecnico.png`,
          c.num_serie,
          'checklist'
        );
      }
      if (this.signaturePadLinea && !this.signaturePadLinea.isEmpty() && !firmaLineaUrl) {
        this._showProgress('Subiendo firma de línea...');
        firmaLineaUrl = await recepcionService.subirFirma(
          await this.toBlob(this.signaturePadLinea),
          `CL-${c.id}_firma_linea.png`,
          c.num_serie,
          'checklist'
        );
      }
      if (this.signaturePadArea && !this.signaturePadArea.isEmpty() && !firmaAreaUrl) {
        this._showProgress('Subiendo firma de área...');
        firmaAreaUrl = await recepcionService.subirFirma(
          await this.toBlob(this.signaturePadArea),
          `CL-${c.id}_firma_area.png`,
          c.num_serie,
          'checklist'
        );
      }

      // Leer responsables de los selects si están montados
      const respLineaSelect = document.getElementById('responsableLinea');
      const respAreaSelect = document.getElementById('responsableArea');
      const tecnicoSelect = document.getElementById('tecnicoEnsamble');

      this._showProgress('Guardando cambios...');
      const payload = {
        items: c.items,
        niveles: c.niveles,
        llantas: c.torques,
        observaciones,
        tecnico_ensamble: tecnicoSelect ? (tecnicoSelect.options[tecnicoSelect.selectedIndex]?.text || c.tecnico_ensamble) : c.tecnico_ensamble,
        tecnico_ensamble_id: tecnicoSelect && tecnicoSelect.value ? parseInt(tecnicoSelect.value, 10) : c.tecnico_ensamble_id,
        tecnico_ensamble_firma_url: firmaTecnicoUrl,
        responsable_linea: respLineaSelect ? (respLineaSelect.options[respLineaSelect.selectedIndex]?.text || c.responsable_linea) : c.responsable_linea,
        responsable_linea_id: respLineaSelect && respLineaSelect.value ? parseInt(respLineaSelect.value, 10) : c.responsable_linea_id,
        responsable_linea_firma_url: firmaLineaUrl,
        responsable_area: respAreaSelect ? (respAreaSelect.options[respAreaSelect.selectedIndex]?.text || c.responsable_area) : c.responsable_area,
        responsable_area_id: respAreaSelect && respAreaSelect.value ? parseInt(respAreaSelect.value, 10) : c.responsable_area_id,
        responsable_area_firma_url: firmaAreaUrl
      };
      const respGuardar = await produccionService.guardarCambiosChecklist(c.id, payload);
      this._hideProgress();
      toast.success('Cambios guardados');

      // Aviso de la sincronización con PNC: detalla creados, eliminados,
      // marcados para revisión y flags limpiados (reverso C→I→C).
      const pncRes = (respGuardar?.data || respGuardar)?.pnc_resultado;
      if (pncRes) {
        const partes = [];
        if (pncRes.creados > 0) {
          const accion = pncRes.creado ? 'PNC nuevo creado' : 'agregados al PNC abierto';
          partes.push(`${pncRes.creados} daño(s) ${accion}`);
        }
        if (pncRes.eliminados > 0) {
          partes.push(`${pncRes.eliminados} eliminado(s) (sin trabajo)`);
        }
        if (pncRes.marcados_revision > 0) {
          partes.push(`${pncRes.marcados_revision} marcado(s) para revisión por calidad`);
        }
        if (pncRes.flags_limpiados > 0) {
          partes.push(`${pncRes.flags_limpiados} reactivado(s) (volvió a Corregir)`);
        }
        if (partes.length > 0) {
          const ref = pncRes.pnc_id ? ` (PNC #${pncRes.pnc_id})` : '';
          toast.info(`Sincronización con PNC${ref}: ${partes.join(', ')}.`);
        }
      }

      const indicator = document.getElementById('unsavedIndicator');
      if (indicator) indicator.style.display = 'none';

      // Recargar checklist para reset de pads + re-render con URLs actualizadas
      // (mismo patrón que OT y Liberaciones). Esto evita re-subida de firmas
      // en saves consecutivos porque cargarChecklist resetea los signaturePads.
      await this.cargarChecklist(c.id);
    } catch (error) {
      this._hideProgress();
      toast.error(error.message || 'Error al guardar cambios');
    }
  },

  _showProgress(message) {
    let overlay = document.getElementById('progressOverlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'progressOverlay';
      overlay.style.cssText = 'position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(255,255,255,0.9); display: flex; flex-direction: column; align-items: center; justify-content: center; z-index: 9999;';
      overlay.innerHTML = `
        <div class="spinner" style="margin-bottom: 1rem;"></div>
        <p id="progressMessage" style="font-size: 1rem; font-weight: 600; color: var(--gray-700);"></p>
      `;
      document.body.appendChild(overlay);
    }
    document.getElementById('progressMessage').textContent = message;
  },

  _hideProgress() {
    const overlay = document.getElementById('progressOverlay');
    if (overlay) overlay.remove();
  },

  async completarChecklist() {
    // Si hay cambios pendientes, exigir guardar primero (patrón Recepciones/OT)
    const indicator = document.getElementById('unsavedIndicator');
    if (indicator && indicator.style.display !== 'none') {
      toast.warning('Guarde los cambios antes de completar el check list');
      return;
    }

    const c = this.data.checklist;

    // Verificar pestañas pendientes
    const pendientes = [];

    const itemsSinEstado = (c.items || []).filter(i => !i.estado);
    const itemsEnsamble = itemsSinEstado.filter(i => (this._categoriasEnsamble || []).includes(i.categoria));
    const itemsFuncionamiento = itemsSinEstado.filter(i => (this._categoriasFuncionamiento || []).includes(i.categoria));

    // Verificar inputs inline vacíos (mediciones) por tab
    const itemsConInputVacio = (c.items || []).filter(i => {
      if (i.estado === 'no_aplica') return false;
      const totalBlancos = (i.item.match(/___+/g) || []).length;
      if (totalBlancos === 0) return false;
      const valores = this.parseValoresItem(i.valores_inline);
      for (let j = 0; j < totalBlancos; j++) {
        if (!valores[j] || !valores[j].toString().trim()) return true;
      }
      return false;
    });
    const inputsEnsamble = itemsConInputVacio.filter(i => (this._categoriasEnsamble || []).includes(i.categoria));
    const inputsFuncionamiento = itemsConInputVacio.filter(i => (this._categoriasFuncionamiento || []).includes(i.categoria));

    // Orden: Ensamble > Torque > Niveles > Funcionamiento > Firmas
    if (itemsEnsamble.length > 0 || inputsEnsamble.length > 0) pendientes.push('el checklist de ensamble');

    const torques = c.torques || {};
    const torqueKeys = [
      'frontal_izq_nm', 'frontal_izq_psi',
      'frontal_der_nm', 'frontal_der_psi',
      'trasero_izq_nm', 'trasero_izq_psi',
      'trasero_der_nm', 'trasero_der_psi'
    ];
    if (torqueKeys.some(k => !torques[k])) pendientes.push('el diagrama de torque');

    const niveles = c.niveles || {};
    const nivelesNA = this._nivelesNoAplican();
    if (this.FLUIDOS_MAP.some(f => !nivelesNA.includes(f.key) && !niveles[f.key])) pendientes.push('los niveles');

    if (itemsFuncionamiento.length > 0 || inputsFuncionamiento.length > 0) pendientes.push('la verificación de funcionamiento');

    // Tab Observaciones: PNC y cualquier categoría fuera de Ensamble/Funcionamiento (el backend exige estado en todos)
    const catsEnTabsPrincipales = new Set([
      ...(this._categoriasEnsamble || []),
      ...(this._categoriasFuncionamiento || [])
    ]);
    const itemsSinEstadoOtros = itemsSinEstado.filter(i => !catsEnTabsPrincipales.has(i.categoria));
    const inputsOtrosTabs = itemsConInputVacio.filter(i => !catsEnTabsPrincipales.has(i.categoria));
    if (itemsSinEstadoOtros.length > 0 || inputsOtrosTabs.length > 0) {
      pendientes.push('las observaciones (incluye producto no conforme)');
    }

    if (pendientes.length > 0) {
      const tabMap = {
        'el checklist de ensamble': 'ensamble',
        'el diagrama de torque': 'torque',
        'los niveles': 'niveles',
        'la verificación de funcionamiento': 'funcionamiento',
        'las observaciones (incluye producto no conforme)': 'observaciones',
        'las firmas': 'firmas'
      };
      const nombre = pendientes[0];
      // Decir QUÉ falta, no sólo dónde. Las secciones se pintan «Completo» contando
      // sólo el estado C/I/—, así que un item con la medición vacía (torque, kg, mm)
      // se ve verde y aun así frena el cierre. Sin esta lista el usuario ve todo en
      // verde y un error que no puede resolver.
      const faltantes = [...itemsEnsamble, ...inputsEnsamble, ...itemsFuncionamiento,
                         ...inputsFuncionamiento, ...itemsSinEstadoOtros, ...inputsOtrosTabs];
      const unicos = [...new Map(faltantes.map(i => [i.item, i])).values()];
      if (unicos.length) {
        const muestra = unicos.slice(0, 3).map(i => '• ' + i.item.replace(/___+/g, '___')).join('\n');
        toast.error(`Faltan ${unicos.length} partida(s) en ${nombre}:\n${muestra}` +
                    (unicos.length > 3 ? `\n…y ${unicos.length - 3} más` : ''));
      } else {
        toast.error(`Favor de completar ${nombre}`);
      }
      const primerTab = tabMap[nombre];
      if (primerTab) this._showTabCL(primerTab);
      return;
    }

    // Verificar que el checklist tenga técnico asignado
    if (!c.tecnico_ensamble_id && !c.tecnico_ensamble) {
      toast.error('El checklist no tiene técnico de ensamble asignado');
      return;
    }

    const responsableLineaId = c.responsable_linea_id;
    const responsableAreaId = c.responsable_area_id;
    const responsableLinea = c.responsable_linea_nombre || c.responsable_linea || '';
    const responsableArea = c.responsable_area_nombre || c.responsable_area || '';

    if (!responsableLineaId || !responsableAreaId) {
      toast.error('El checklist no tiene responsables asignados. Verifique los datos.');
      return;
    }

    // Validar las 3 firmas (deben estar persistidas como URL)
    if (!c.tecnico_ensamble_firma_url) {
      toast.error('Falta firma del técnico');
      this._showTabCL('firmas');
      return;
    }
    if (!c.responsable_linea_firma_url) {
      toast.error('Falta firma de responsable de línea');
      this._showTabCL('firmas');
      return;
    }
    if (!c.responsable_area_firma_url) {
      toast.error('Falta firma de responsable de área');
      this._showTabCL('firmas');
      return;
    }

    modal.confirm(
      'Completar Check List',
      '¿Está seguro de completar este check list? Una vez completado no podrá modificarse.',
      async () => {
        try {
          // Firmas ya persistidas (guardadas con "Guardar cambios"). Usar las URLs actuales.
          const firmaTecnicoUrl = c.tecnico_ensamble_firma_url;
          const firmaLineaUrl = c.responsable_linea_firma_url;
          const firmaAreaUrl = c.responsable_area_firma_url;

          // Tab Firmas no monta el textarea #observaciones; usar fallback en memoria (mismo criterio que guardarCambios)
          const observacionesEl = document.getElementById('observaciones');
          const observaciones = observacionesEl
            ? observacionesEl.value
            : (this.data.checklist.observaciones || '');

          this._showProgress('Completando check list...');
          const respCompletar = await produccionService.completarChecklist(c.id, {
            tecnico_ensamble: c.tecnico_ensamble,
            tecnico_ensamble_id: c.tecnico_ensamble_id,
            tecnico_ensamble_firma_url: firmaTecnicoUrl,
            responsable_linea: responsableLinea,
            responsable_linea_id: parseInt(responsableLineaId, 10),
            responsable_linea_firma_url: firmaLineaUrl,
            responsable_area: responsableArea,
            responsable_area_id: parseInt(responsableAreaId, 10),
            responsable_area_firma_url: firmaAreaUrl,
            observaciones
          });

          this._hideProgress();
          toast.success('Check list completado exitosamente');

          // Aviso si se generaron / actualizaron problemas en PNC
          const pncRes = (respCompletar?.data || respCompletar)?.pnc_resultado;
          if (pncRes && pncRes.problemas_agregados > 0) {
            const accion = pncRes.creado ? 'se creó un PNC nuevo' : 'se agregaron problemas al PNC abierto';
            toast.info(`Items con "Corregir": ${accion} (#${pncRes.pnc_id}) con ${pncRes.problemas_agregados} problema(s).`);
          }
          await this.cargarChecklist(c.id);
        } catch (error) {
          this._hideProgress();
          // 409: lista de partidas sin C/I/— (detalle viene en error.details desde el API)
          const pend = error.details?.items;
          if (pend && pend.length) {
            const esc = (s) => String(s || '')
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;');
            const lista = pend.map((i) => `<li style="margin:0.35rem 0;font-size:0.9rem;">${esc(i.texto)} <span style="color:var(--gray-500);font-size:0.75rem;">(${esc(i.categoria)})</span></li>`).join('');
            modal.open(
              'Partidas pendientes de verificar',
              `<p style="margin-bottom:0.75rem;">${esc(error.message)}</p><ul style="text-align:left;margin:0;padding-left:1.1rem;max-height:50vh;overflow:auto;">${lista}</ul>`,
              [{ text: 'Entendido', class: 'btn-primary' }]
            );
            if (pend.some((i) => i.categoria === this.PNC_CATEGORIA)) {
              this._showTabCL('observaciones');
            }
          } else {
            toast.error(error.message || 'Error al completar check list');
          }
        }
      }
    );
  },

  toggleSeccion(categoria) {
    const checkboxes = document.querySelectorAll(`.chk-${categoria}`);
    const todos = Array.from(checkboxes);
    const todosSeleccionados = todos.every(cb => cb.checked);
    todos.forEach(cb => cb.checked = !todosSeleccionados);
  },

  async agregarItemsPlantilla() {
    if (!this.data.plantilla) {
      await this.cargarPlantilla();
    }

    // Agregar items de la plantilla al checklist actual
    const checklistId = this.data.checklist.id;

    try {
      for (const [categoria, items] of Object.entries(this.data.plantilla)) {
        for (const item of items) {
          await produccionService.agregarItemChecklist(checklistId, {
            categoria,
            item
          });
        }
      }
      toast.success('Items agregados exitosamente');
      await this.cargarChecklist(checklistId);
    } catch (error) {
      toast.error('Error al agregar items');
      console.error(error);
    }
  },

  /** Solo ítems PNC (tab Observaciones); evita duplicar si ya existen */
  async agregarItemsPNCDesdePlantilla() {
    if (!this.data.plantilla) {
      await this.cargarPlantilla();
    }
    const checklistId = this.data.checklist.id;
    const cat = this.PNC_CATEGORIA;
    const textosPlantilla = this.data.plantilla?.[cat];
    if (!textosPlantilla?.length) {
      toast.error('La plantilla no incluye ítems PNC');
      return;
    }
    const existentes = new Set(
      (this.data.checklist.items || []).filter(i => i.categoria === cat).map(i => i.item)
    );
    try {
      let agregados = 0;
      for (const item of textosPlantilla) {
        if (existentes.has(item)) continue;
        await produccionService.agregarItemChecklist(checklistId, { categoria: cat, item });
        agregados++;
      }
      if (agregados === 0) {
        toast.info('Los ítems PNC ya estaban en el checklist');
      } else {
        toast.success(`${agregados} ítem(s) PNC agregado(s)`);
      }
      await this.cargarChecklist(checklistId);
      this._showTabCL('observaciones');
    } catch (error) {
      toast.error(error.message || 'Error al agregar ítems PNC');
    }
  },

  // --- Niveles de Fluidos ---

  FLUIDOS_MAP: [
    { display: 'Nivel de Combustible', key: 'combustible' },
    { display: 'Nivel de Anticongelante', key: 'anticongelante' },
    { display: 'Nivel de Aceite de Sistema Hidráulico', key: 'aceite_sistema_hidraulico' },
    { display: 'Nivel de Aceite de Motor', key: 'aceite_motor' },
    { display: 'Nivel de Aceite de Transmisión', key: 'aceite_transmision' },
    { display: 'Nivel de Aceite de Sistema de Dirección', key: 'aceite_sistema_direccion' },
    { display: 'Nivel de Aceite de Frenos', key: 'aceite_frenos' },
    { display: 'Nivel de Aceite de Eje Delantero', key: 'aceite_eje_delantero' },
    { display: 'Nivel de Aceite de Maza Izquierda', key: 'aceite_maza_izquierda' },
    { display: 'Nivel de Aceite de Maza Derecha', key: 'aceite_maza_derecha' },
    { display: 'Nivel de Aceite del Filtro de Aire', key: 'aceite_filtro_aire' }
  ],

  SEGMENTO_A_NIVEL: {
    1: 'min', 2: 'bajo', 3: 'medio_bajo', 4: 'medio', 5: 'medio_alto', 6: 'alto', 7: 'max'
  },

  NIVEL_A_SEGMENTO: {
    'min': 1, 'bajo': 2, 'medio_bajo': 3, 'medio': 4, 'medio_alto': 5, 'alto': 6, 'max': 7
  },

  FLUIDOS_COLORES: {
    combustible:               { 1:'red', 4:'yellow', 7:'green' },
    anticongelante:            { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_sistema_hidraulico: { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_motor:              { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_transmision:        { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_sistema_direccion:  { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_frenos:             { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_eje_delantero:      { 1:'red', 4:'green', 5:'green', 7:'red' },
    aceite_maza_izquierda:     { 1:'red', 4:'green', 7:'red' },
    aceite_maza_derecha:       { 1:'red', 4:'green', 7:'red' },
    aceite_filtro_aire:        { 1:'red', 4:'green', 5:'green', 7:'red' }
  },

  // Niveles que NO aplican para el modelo del checklist. Config DINÁMICA: viene del
  // backend (columna `modelos.niveles_no_aplican`) ya resuelta en `checklist.niveles_no_aplican`.
  _nivelesNoAplican() {
    const na = this.data.checklist?.niveles_no_aplican;
    return Array.isArray(na) ? na : [];
  },

  renderNiveles(c) {
    const niveles = c.niveles || {};
    const noAplican = this._nivelesNoAplican();

    const getSegmento = (key) => {
      return this.NIVEL_A_SEGMENTO[niveles[key]] || 0;
    };

    return `
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <h3 style="margin-bottom: 1rem;">Niveles de Fluidos</h3>
        <div class="nivel-fluidos-grid">
          ${this.FLUIDOS_MAP.map(f =>
            this.renderNivelBar(f.key, f.display, getSegmento(f.key), noAplican.includes(f.key))
          ).join('')}
        </div>
      </div>
    `;
  },

  renderNivelBar(fluidoKey, fluidoDisplay, segmentoActivo, noAplica = false) {
    // Nivel que no aplica para este modelo: se muestra tachado, gris y no editable.
    if (noAplica) {
      return `
        <div class="nivel-bar" style="opacity:0.6;">
          <div class="nivel-bar-title" style="text-decoration:line-through;color:var(--gray-400);">${fluidoDisplay}</div>
          <div style="text-align:center;padding:0.9rem 0;color:#6c757d;font-weight:700;font-size:0.85rem;">No aplica</div>
        </div>
      `;
    }
    const isReadOnly = this.data.isReadOnly;
    const colores = this.FLUIDOS_COLORES[fluidoKey] || {};
    const segments = [1, 2, 3, 4, 5, 6, 7].map(seg => {
      const isActive = segmentoActivo === seg;
      const disabledClass = isReadOnly ? 'disabled' : '';
      const activeClass = isActive ? 'active' : '';
      const colorClass = colores[seg] ? `seg-${colores[seg]}` : '';
      const onclick = isReadOnly ? '' : `onclick="window.checklistDetalleView.onSegmentClick('${fluidoKey}', ${seg})"`;
      return `<div class="nivel-segment ${colorClass} ${activeClass} ${disabledClass}" ${onclick} title="Segmento ${seg}"></div>`;
    }).join('');

    return `
      <div class="nivel-bar">
        <div class="nivel-bar-title">${fluidoDisplay}</div>
        <div class="nivel-segments">
          ${segments}
        </div>
        <div class="nivel-labels">
          <span>Min</span>
          <span>Max</span>
        </div>
      </div>
    `;
  },

  onSegmentClick(fluidoKey, segmento) {
    const nivelVisual = this.SEGMENTO_A_NIVEL[segmento];
    if (!nivelVisual) return;

    // Actualizar datos locales
    if (!this.data.checklist.niveles) this.data.checklist.niveles = {};
    this.data.checklist.niveles[fluidoKey] = nivelVisual;

    // Re-renderizar tab niveles
    this._showTabCL('niveles');
    this._showUnsaved();
  },

  // --- Items con inputs inline (reemplazo de ___) ---

  parseValoresItem(data) {
    if (!data) return [];
    // JSON column auto-parsed by mysql2 driver
    if (Array.isArray(data)) return data;
    // String (after local save before reload)
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) return parsed;
    } catch (e) { /* no es JSON */ }
    return [];
  },

  renderItemConInputs(item) {
    const texto = item.item || '';
    const obsText = item.observacion ? `<div style="font-size: 0.75rem; color: var(--text-muted); margin-top: 0.25rem;">${item.observacion}</div>` : '';

    if (!texto.includes('___')) {
      return texto + obsText;
    }

    const valores = this.parseValoresItem(item.valores_inline);
    const isReadOnly = this.data.isReadOnly;
    let idx = 0;

    const resultado = texto.replace(/___+/g, (match, offset) => {
      const val = valores[idx] || '';
      const currentIdx = idx;
      const placeholder = '---';
      idx++;
      return `<input type="number" class="item-inline-input" value="${val.replace(/"/g, '&quot;')}"
        placeholder="${placeholder}"
        min="0" step="any"
        ${isReadOnly ? 'readonly' : ''}
        onchange="window.checklistDetalleView.guardarValorItem(${item.id}, ${currentIdx}, this.value)"
      />`;
    });

    return resultado + obsText;
  },

  guardarValorItem(itemId, inputIndex, value) {
    // Buscar el item en los datos locales
    const item = this.data.checklist.items.find(i => i.id === itemId);
    if (!item) return;

    // Parsear valores actuales
    let valores = this.parseValoresItem(item.valores_inline);
    const totalBlancos = (item.item.match(/___+/g) || []).length;

    // Asegurar que el array tenga el tamaño correcto
    while (valores.length < totalBlancos) valores.push('');

    valores[inputIndex] = value;
    item.valores_inline = JSON.stringify(valores);

    this._showUnsaved();
  },

  // --- Torques (Nm/PSI) ---

  TORQUES_CAMPOS: [
    { key: 'frontal_izq_nm', label: 'Nm', pos: 'frontal_izq' },
    { key: 'frontal_izq_psi', label: 'PSI', pos: 'frontal_izq' },
    { key: 'frontal_der_nm', label: 'Nm', pos: 'frontal_der' },
    { key: 'frontal_der_psi', label: 'PSI', pos: 'frontal_der' },
    { key: 'trasero_izq_nm', label: 'Nm', pos: 'trasero_izq' },
    { key: 'trasero_izq_psi', label: 'PSI', pos: 'trasero_izq' },
    { key: 'trasero_der_nm', label: 'Nm', pos: 'trasero_der' },
    { key: 'trasero_der_psi', label: 'PSI', pos: 'trasero_der' },
  ],

  renderTorques(c) {
    const torques = c.torques || {};
    const isReadOnly = this.data.isReadOnly;

    const renderInput = (key, placeholder) => {
      const val = torques[key] || '';
      return `<input type="number" class="torque-input" id="torque_${key}" value="${val}"
        placeholder="${placeholder}" min="0" step="any" ${isReadOnly ? 'readonly' : ''}
        onchange="window.checklistDetalleView.guardarTorque('${key}', this.value)" />`;
    };

    const renderGroup = (titulo, nmKey, psiKey) => {
      return `
        <div class="torque-group">
          <div class="torque-group-title">${titulo}</div>
          <div class="torque-group-inputs">
            <div class="torque-input-wrap">
              <label class="torque-input-label">Nm</label>
              ${renderInput(nmKey, '---')}
            </div>
            <div class="torque-input-wrap">
              <label class="torque-input-label">PSI</label>
              ${renderInput(psiKey, '---')}
            </div>
          </div>
        </div>`;
    };

    return `
      <div style="background: var(--bg-secondary); padding: 1rem; border-radius: 8px; margin-bottom: 1.5rem;">
        <div class="torque-diagram">
          <div class="torque-sides">
            <div class="torque-side">
              ${renderGroup('Frontal Izquierdo', 'frontal_izq_nm', 'frontal_izq_psi')}
              ${renderGroup('Trasero Izquierdo', 'trasero_izq_nm', 'trasero_izq_psi')}
            </div>
            <div class="torque-tractor">
              <img src="/img/tractor-neumatico.png" alt="Tractor" onerror="this.style.display='none'">
            </div>
            <div class="torque-side">
              ${renderGroup('Frontal Derecho', 'frontal_der_nm', 'frontal_der_psi')}
              ${renderGroup('Trasero Derecho', 'trasero_der_nm', 'trasero_der_psi')}
            </div>
          </div>
        </div>
      </div>
    `;
  },

  guardarTorque(key, value) {
    if (!this.data.checklist.torques) this.data.checklist.torques = {};
    this.data.checklist.torques[key] = value;
    this._showUnsaved();
  },

  // --- Métodos de firma digital ---

  loadSignaturePad() {
    if (typeof SignaturePad !== 'undefined') {
      this.initFirmasCompletar();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/signature_pad/4.1.7/signature_pad.umd.min.js';
    script.onload = () => this.initFirmasCompletar();
    script.onerror = () => {
      toast.error('Error al cargar módulo de firmas. Recargue la página.');
    };
    document.head.appendChild(script);
  },

  initFirmasCompletar() {
    const canvasTecnico = document.getElementById('firma-tecnico');
    const canvasLinea = document.getElementById('firma-linea');
    const canvasArea = document.getElementById('firma-area');

    // Salir solo si ningún canvas está disponible
    if (!canvasTecnico && !canvasLinea && !canvasArea) return;

    const resizeCanvas = (canvas) => {
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = canvas.offsetWidth * ratio;
      canvas.height = canvas.offsetHeight * ratio;
      canvas.getContext('2d').scale(ratio, ratio);
    };

    const initPad = (canvas) => {
      resizeCanvas(canvas);
      const pad = new SignaturePad(canvas, {
        backgroundColor: 'rgb(255, 255, 255)',
        penColor: 'rgb(0, 0, 0)'
      });

      const hidePlaceholder = () => {
        const placeholder = canvas.parentElement.querySelector('.firma-placeholder');
        if (placeholder) placeholder.style.display = 'none';
      };
      canvas.addEventListener('pointerdown', hidePlaceholder);
      canvas.addEventListener('touchstart', hidePlaceholder);

      // Activar "Hay cambios sin guardar" al terminar un trazo
      pad.addEventListener('endStroke', () => this._showUnsaved());

      return pad;
    };

    this.signaturePadTecnico = canvasTecnico ? initPad(canvasTecnico) : null;
    this.signaturePadLinea = canvasLinea ? initPad(canvasLinea) : null;
    this.signaturePadArea = canvasArea ? initPad(canvasArea) : null;
  },

  limpiarFirmaCompletar(tipo) {
    const padMap = {
      tecnico: this.signaturePadTecnico,
      linea: this.signaturePadLinea,
      area: this.signaturePadArea
    };
    const canvasMap = {
      tecnico: 'firma-tecnico',
      linea: 'firma-linea',
      area: 'firma-area'
    };

    const pad = padMap[tipo];
    if (pad) {
      pad.clear();
      const canvas = document.getElementById(canvasMap[tipo]);
      const placeholder = canvas?.parentElement?.querySelector('.firma-placeholder');
      if (placeholder) placeholder.style.display = '';
    }
  },

  async toBlob(signaturePad) {
    const dataURL = signaturePad.toDataURL('image/png');
    const response = await fetch(dataURL);
    return response.blob();
  }
};
